import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import type { Json } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  Plus,
  Play,
  Trash2,
  Edit3,
  Zap,
  Globe,
  Database,
  GitBranch,
  Mail,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  ArrowDown,
  X,
  Sparkles,
  Send,
  Bot,
  User,
  Bug,
  Copy,
  RefreshCw,
  Repeat,
  Terminal,
  LayoutTemplate,
  Search,
  Filter,
  Square,
  History,
  ListChecks,
  Settings2,
  Users,
  Paperclip,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { AutocompleteInput, buildVariableSuggestions, buildPlainSuggestions } from "@/components/AutocompleteInput";
import ConversationDebugDialog from "@/components/ConversationDebugDialog";
import QueryEditor from "@/components/admin/QueryEditor";
import { useAuditLog } from "@/hooks/useAuditLog";
import { toCamelCase } from "@/lib/text-utils";
import { TemplateBrowserDialog, TemplatePromptDialog } from "@/components/TemplateBrowserDialog";
import { INDUSTRIES, TEMPLATE_CATEGORIES, type WorkflowTemplate } from "@/lib/template-constants";

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────
type NodeType = "trigger" | "http_request" | "run_query" | "condition" | "send_email" | "loop";
type ExecStatus = "pending" | "running" | "success" | "failed";

interface ConditionBranch {
  id: string;
  label: string;
  expression: string;
}

interface WorkflowNode {
  id: string;
  type: NodeType;
  label?: string;
  depends_on: string[];
  // Optional routing: this node only runs when the referenced condition
  // node matches the specified branch ("default" = no branch matched).
  condition_branch?: {
    condition_node_id: string;
    branch_id: string;
  };
  // When set, this node runs inside a loop – once per row from the
  // loop node's query. Row columns are available as {{loop_node_id.column}}.
  loop_source?: string;
  config: Record<string, unknown>;
}

interface WorkflowDefinition {
  nodes: WorkflowNode[];
}

interface WorkflowDraft {
  name: string;
  description: string;
  nodes: WorkflowNode[];
  editingId: string | null;
}

interface Workflow {
  id: string;
  name: string;
  description: string | null;
  definition: WorkflowDefinition;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_run_at?: string | null;
  department_id?: string | null;
}

interface Department {
  id: string;
  name: string;
}

// Validate a 5-field cron expression (minute hour dom month dow)
function isValidCron(expr: string): boolean {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  const patterns = [
    /^(\*|(\d{1,2})([-/]\d{1,2})?)(,(\*|(\d{1,2})([-/]\d{1,2})?))*$/, // minute
    /^(\*|(\d{1,2})([-/]\d{1,2})?)(,(\*|(\d{1,2})([-/]\d{1,2})?))*$/, // hour
    /^(\*|(\d{1,2})([-/]\d{1,2})?)(,(\*|(\d{1,2})([-/]\d{1,2})?))*$/, // dom
    /^(\*|(\d{1,2})([-/]\d{1,2})?)(,(\*|(\d{1,2})([-/]\d{1,2})?))*$/, // month
    /^(\*|(\d{1})([-/]\d{1})?)(,(\*|(\d{1})([-/]\d{1})?))*$/,          // dow
  ];
  return parts.every((p, i) => patterns[i].test(p));
}

function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "Never";
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  if (diffSecs < 60) return "just now";
  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

interface NodeExecution {
  id: string;
  node_id: string;
  node_type: string;
  status: ExecStatus;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
}

interface WorkflowExecution {
  id: string;
  workflow_id: string;
  status: ExecStatus;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  tid: string | null;
}

// ────────────────────────────────────────────────────────────
// Node type metadata
// ────────────────────────────────────────────────────────────
const NODE_META: Record<
  NodeType,
  { label: string; icon: React.ComponentType<{ className?: string }>; color: string; bg: string; description: string }
> = {
  trigger: {
    label: "Trigger",
    icon: Zap,
    color: "text-amber-600",
    bg: "bg-amber-50 border-amber-200",
    description: "Starts the workflow on an event",
  },
  http_request: {
    label: "HTTP Request",
    icon: Globe,
    color: "text-blue-600",
    bg: "bg-blue-50 border-blue-200",
    description: "Calls an external API endpoint",
  },
  run_query: {
    label: "Run Query",
    icon: Database,
    color: "text-indigo-600",
    bg: "bg-indigo-50 border-indigo-200",
    description: "Runs a SQL SELECT against your database",
  },
  condition: {
    label: "Condition",
    icon: GitBranch,
    color: "text-purple-600",
    bg: "bg-purple-50 border-purple-200",
    description: "Evaluates an expression on previous node output",
  },
  send_email: {
    label: "Send Email",
    icon: Mail,
    color: "text-green-600",
    bg: "bg-green-50 border-green-200",
    description: "Sends an email via Resend",
  },
  loop: {
    label: "Loop",
    icon: Repeat,
    color: "text-orange-600",
    bg: "bg-orange-50 border-orange-200",
    description: "Iterates downstream nodes for each row of a query",
  },
};

// ────────────────────────────────────────────────────────────
// Default configs
// ────────────────────────────────────────────────────────────
function defaultConfig(type: NodeType): Record<string, unknown> {
  switch (type) {
    case "trigger":
      return { event: "manual" };
    case "http_request":
      return { url: "", method: "GET", headers: {}, body: {} };
    case "run_query":
      return { query: "", label: "" };
    case "condition":
      return { branches: [{ id: "branch_1", label: "Branch 1", expression: "" }] };
    case "send_email":
      return { to: "", subject: "", body: "" };
    case "loop":
      return { query: "", label: "", parallel: true, max_concurrency: 5 };
  }
}

// Workflow Template types & constants imported from @/lib/template-constants

// ────────────────────────────────────────────────────────────
// Status badge
// ────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: ExecStatus }) {
  const colorMap: Record<ExecStatus, string> = {
    pending: "bg-muted text-muted-foreground",
    running: "bg-blue-100 text-blue-700 border border-blue-300",
    success: "bg-green-100 text-green-700 border border-green-300",
    failed: "bg-red-100 text-red-700 border border-red-300",
  };
  const icons: Record<ExecStatus, React.ReactNode> = {
    pending: <Clock className="h-3 w-3" />,
    running: <Loader2 className="h-3 w-3 animate-spin" />,
    success: <CheckCircle2 className="h-3 w-3" />,
    failed: <XCircle className="h-3 w-3" />,
  };
  const labels: Record<ExecStatus, string> = {
    pending: "Pending",
    running: "Running",
    success: "Success",
    failed: "Failed",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${colorMap[status]}`}>
      {icons[status]} {labels[status]}
    </span>
  );
}

// ────────────────────────────────────────────────────────────
// Variable help block – shows available upstream variables
// ────────────────────────────────────────────────────────────
const FIELD_HINTS: Record<NodeType, string[]> = {
  trigger: ["event", "triggered_at", "data"],
  http_request: ["ok", "status", "status_text", "body"],
  run_query: ["rows", "row_count", "query", "label"],
  condition: ["passed", "matched_branch", "branches"],
  send_email: ["sent", "to", "subject", "message_id"],
  loop: ["rows", "row_count", "query", "label", "<column_name>"],
};

// Extract all dot-notation paths from a JSON value, up to a max depth
function extractJsonPaths(obj: unknown, prefix = "", depth = 0, maxDepth = 3): string[] {
  if (depth >= maxDepth || obj === null || obj === undefined) return [];
  if (typeof obj !== "object") return [];

  const paths: string[] = [];

  if (Array.isArray(obj)) {
    // For arrays, peek at the first element to show [0].key paths
    if (obj.length > 0) {
      const childPaths = extractJsonPaths(obj[0], `${prefix}[0]`, depth + 1, maxDepth);
      paths.push(...childPaths);
    }
    return paths;
  }

  for (const [key, val] of Object.entries(obj as Record<string, unknown>)) {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    paths.push(fullPath);
    if (val !== null && typeof val === "object") {
      paths.push(...extractJsonPaths(val, fullPath, depth + 1, maxDepth));
    }
  }
  return paths;
}

// Render a JSON value as a human-readable mini-tree
function JsonTree({ data, nodeId }: { data: unknown; nodeId: string }) {
  const [expanded, setExpanded] = useState(false);

  const paths = (data !== null && data !== undefined && typeof data === "object")
    ? extractJsonPaths(data)
    : [];

  if (paths.length === 0) return null;

  const preview = paths.slice(0, 3).map((p) => `{{${nodeId}.${p}}}`).join(", ");
  const hasMore = paths.length > 3;

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {expanded ? "Hide" : "Show"} available paths ({paths.length})
      </button>
      {!expanded && (
        <p className="text-xs text-muted-foreground mt-0.5 truncate">
          {preview}{hasMore ? " …" : ""}
        </p>
      )}
      {expanded && (
        <div className="mt-1 rounded border bg-background p-2 space-y-0.5 max-h-[160px] overflow-y-auto">
          {paths.map((p) => {
            const resolved = resolvePathPreview(p, data);
            return (
              <div key={p} className="flex items-baseline gap-2 text-xs font-mono">
                <code className="text-primary shrink-0">{`{{${nodeId}.${p}}}`}</code>
                {resolved !== undefined && (
                  <span className="text-muted-foreground truncate max-w-[200px]">
                    = {typeof resolved === "object" ? JSON.stringify(resolved) : String(resolved)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Resolve a path like "rows[0].email" against a data object for preview
function resolvePathPreview(path: string, data: unknown): unknown {
  const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  let current: unknown = data;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function VariableHelpBlock({
  priorNodes,
  lastOutputs,
}: {
  priorNodes: WorkflowNode[];
  lastOutputs?: Record<string, unknown>;
}) {
  if (priorNodes.length === 0) return null;
  return (
    <div className="rounded-md bg-muted/50 border p-2.5 space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">
        💡 Use <code className="bg-muted px-1 rounded text-foreground">{"{{node_id.field}}"}</code> in any text field to inject values from upstream nodes.
      </p>
      <div className="space-y-1">
        {priorNodes.map((n) => {
          const output = lastOutputs?.[n.id];
          return (
            <div key={n.id} className="text-xs text-muted-foreground">
              <div>
                <span className="font-medium text-foreground">{n.id}</span>
                <span className="ml-1 text-muted-foreground">({NODE_META[n.type]?.label})</span>
                {!output && (
                  <>
                    :{" "}
                    {(FIELD_HINTS[n.type] ?? []).map((f) => (
                      <code key={f} className="bg-muted px-1 rounded mr-1">{`{{${n.id}.${f}}}`}</code>
                    ))}
                  </>
                )}
              </div>
              {output && <JsonTree data={output} nodeId={n.id} />}
            </div>
          );
        })}
      </div>
      {lastOutputs && Object.keys(lastOutputs).length > 0 && (
        <p className="text-xs text-muted-foreground/60 italic">
          Paths above are based on the last execution output.
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Run Query node form with Test Run capability
// ────────────────────────────────────────────────────────────
function RunQueryNodeForm({
  queryText,
  queryName,
  labelText,
  selfId,
  onSetLabel,
  onAddQuery,
}: {
  queryText: string;
  queryName?: string;
  labelText: string;
  selfId: string;
  onSetLabel: (v: string) => void;
  onAddQuery?: () => void;
}) {
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<Record<string, unknown>[] | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const displaySql = queryText || "";
  const displayName = queryName || "";

  const handleTestRun = async () => {
    if (!displaySql.trim()) {
      toast.error("No SQL query to test");
      return;
    }
    setTesting(true);
    setTestResults(null);
    setTestError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();
      // Wrap with LIMIT for safety
      const testSql = displaySql.trim().replace(/;\s*$/, "");
      const wrappedSql = /\bLIMIT\b/i.test(testSql) ? testSql : `${testSql} LIMIT 100`;

      const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/execute/queries`, {
        method: "POST",
        headers,
        body: JSON.stringify([wrappedSql]),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Request failed" }));
        throw new Error(err.error || `Request failed with status ${res.status}`);
      }

      const data = await res.json();
      let rows: Record<string, unknown>[] = [];
      if (Array.isArray(data)) {
        const first = data[0];
        rows = Array.isArray(first) ? first : (first?.rows || first?.result || data);
      } else if (data?.results) {
        const first = Array.isArray(data.results) ? data.results[0] : data.results;
        rows = Array.isArray(first) ? first : (first?.rows || first?.result || []);
      } else if (data?.rows) {
        rows = data.rows;
      }
      if (!Array.isArray(rows)) rows = [];

      setTestResults(rows);
      setShowResults(true);
    } catch (err: unknown) {
      setTestError(err instanceof Error ? err.message : "Test run failed");
      setShowResults(true);
    } finally {
      setTesting(false);
    }
  };

  const columns = testResults && testResults.length > 0 ? Object.keys(testResults[0]) : [];

  return (
    <>
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Label (optional)</label>
          <Input
            className="h-8 text-sm"
            placeholder="e.g. Fetch overdue invoices"
            value={labelText}
            onChange={(e) => onSetLabel(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Query</label>
            <div className="flex items-center gap-2">
              {displaySql && (
                <button
                  type="button"
                  onClick={handleTestRun}
                  disabled={testing}
                  className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium transition-colors disabled:opacity-50"
                >
                  {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                  Test Run
                </button>
              )}
              {onAddQuery && (
                <button
                  type="button"
                  onClick={onAddQuery}
                  className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                >
                  <Edit3 className="h-3 w-3" /> {displaySql ? "Edit Query" : "New Query"}
                </button>
              )}
            </div>
          </div>
          {displaySql ? (
            <div className="space-y-1.5">
              {displayName && (
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5">
                  <Database className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
                  <span className="text-xs font-medium truncate">{displayName}</span>
                </div>
              )}
              <pre className="text-xs font-mono bg-muted/50 border rounded-md p-3 whitespace-pre-wrap break-all text-muted-foreground max-h-[120px] overflow-y-auto">
                {displaySql}
              </pre>
            </div>
          ) : (
            <div className="flex items-center justify-center border border-dashed rounded-md p-4 text-xs text-muted-foreground">
              No query yet.{" "}
              {onAddQuery && (
                <button type="button" onClick={onAddQuery} className="text-indigo-600 hover:underline ml-1 font-medium">
                  Create a query
                </button>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Results are available to downstream nodes as <code className="bg-muted px-1 rounded">{selfId}.rows</code> and{" "}
            <code className="bg-muted px-1 rounded">{selfId}.row_count</code>.
          </p>
        </div>
      </div>

      {/* Test Results Dialog */}
      <Dialog open={showResults} onOpenChange={setShowResults}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
            <DialogTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4 text-indigo-600" />
              Test Run Results
              {testResults && (
                <Badge variant="secondary" className="text-xs ml-1">
                  {testResults.length} row{testResults.length !== 1 ? "s" : ""}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
            {testError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4">
                <p className="text-sm font-medium text-destructive mb-1">Query Error</p>
                <pre className="text-xs text-destructive/80 whitespace-pre-wrap">{testError}</pre>
              </div>
            ) : testResults && testResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Database className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Query returned no rows</p>
              </div>
            ) : testResults && columns.length > 0 ? (
              <div className="rounded-md border overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      {columns.map((col) => (
                        <th key={col} className="px-3 py-2 text-left font-semibold text-foreground whitespace-nowrap">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {testResults.map((row, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                        {columns.map((col) => (
                          <td key={col} className="px-3 py-1.5 text-muted-foreground whitespace-nowrap max-w-[200px] truncate">
                            {row[col] === null ? (
                              <span className="italic text-muted-foreground/50">null</span>
                            ) : typeof row[col] === "object" ? (
                              JSON.stringify(row[col])
                            ) : (
                              String(row[col])
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>

          <div className="px-6 py-3 border-t shrink-0 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowResults(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Per-node config form
// ────────────────────────────────────────────────────────────
function NodeConfigForm({
  type,
  config,
  onChange,
  allNodes,
  selfId,
  onAddQuery,
  lastOutputs,
}: {
  type: NodeType;
  config: Record<string, unknown>;
  onChange: (cfg: Record<string, unknown>) => void;
  allNodes: WorkflowNode[];
  selfId: string;
  onAddQuery?: () => void;
  lastOutputs?: Record<string, unknown>;
}) {
  const set = (key: string, value: unknown) => onChange({ ...config, [key]: value });
  // Nodes that come before this one (can be referenced in expressions)
  const priorNodes = allNodes.filter((n) => n.id !== selfId);
  const suggestions = buildVariableSuggestions(priorNodes, FIELD_HINTS, lastOutputs);

  if (type === "trigger") {
    const event = String(config.event ?? "manual");
    const cronPreset = String(config.cron_preset ?? "every_hour");
    const cronHour = Number(config.cron_hour ?? 9);
    const cronDay = Number(config.cron_day ?? 1);
    const cronCustom = String(config.cron ?? "");
    const scheduleTz = String(config.schedule_timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC");

    const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const HOURS = Array.from({ length: 24 }, (_, i) => i);

    const BASE_TIMEZONES = [
      "UTC",
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Los_Angeles",
      "America/Sao_Paulo",
      "Europe/London",
      "Europe/Paris",
      "Europe/Berlin",
      "Asia/Dubai",
      "Asia/Kolkata",
      "Asia/Shanghai",
      "Asia/Tokyo",
      "Asia/Singapore",
      "Australia/Sydney",
      "Pacific/Auckland",
    ];
    // Ensure the browser's timezone is always in the list so the dropdown never shows blank
    const COMMON_TIMEZONES = BASE_TIMEZONES.includes(scheduleTz)
      ? BASE_TIMEZONES
      : [scheduleTz, ...BASE_TIMEZONES];

    const PRESETS: { label: string; value: string; needsTime: boolean; needsDay: boolean }[] = [
      { label: "Every 15 minutes", value: "every_15min", needsTime: false, needsDay: false },
      { label: "Every 30 minutes", value: "every_30min", needsTime: false, needsDay: false },
      { label: "Every hour",       value: "every_hour",  needsTime: false, needsDay: false },
      { label: "Every 2 hours",    value: "every_2h",    needsTime: false, needsDay: false },
      { label: "Every 6 hours",    value: "every_6h",    needsTime: false, needsDay: false },
      { label: "Every 12 hours",   value: "every_12h",   needsTime: false, needsDay: false },
      { label: "Daily",            value: "daily",       needsTime: true,  needsDay: false },
      { label: "Weekly",           value: "weekly",      needsTime: true,  needsDay: true  },
      { label: "Monthly (1st)",    value: "monthly",     needsTime: true,  needsDay: false },
      { label: "Custom",           value: "custom",      needsTime: false, needsDay: false },
    ];

    const currentPreset = PRESETS.find((p) => p.value === cronPreset) ?? PRESETS[2];

    function buildCronExpr(preset: string, hour: number, day: number, custom: string): string {
      switch (preset) {
        case "every_15min": return "*/15 * * * *";
        case "every_30min": return "*/30 * * * *";
        case "every_hour":  return "0 * * * *";
        case "every_2h":    return "0 */2 * * *";
        case "every_6h":    return "0 */6 * * *";
        case "every_12h":   return "0 */12 * * *";
        case "daily":       return `0 ${hour} * * *`;
        case "weekly":      return `0 ${hour} * * ${day}`;
        case "monthly":     return `0 ${hour} 1 * *`;
        default:            return custom;
      }
    }

    function describeCronPreset(preset: string, hour: number, day: number, custom: string): string {
      const h = `${hour.toString().padStart(2, "0")}:00`;
      switch (preset) {
        case "every_15min": return "Runs every 15 minutes";
        case "every_30min": return "Runs every 30 minutes";
        case "every_hour":  return "Runs every hour";
        case "every_2h":    return "Runs every 2 hours";
        case "every_6h":    return "Runs every 6 hours";
        case "every_12h":   return "Runs every 12 hours";
        case "daily":       return `Runs every day at ${h}`;
        case "weekly":      return `Runs every ${DAYS[day]} at ${h}`;
        case "monthly":     return `Runs on the 1st of every month at ${h}`;
        case "custom":      return custom ? `Schedule: ${custom}` : "Enter a cron expression below";
        default:            return "";
      }
    }

    const applyPreset = (preset: string, hour: number, day: number, custom?: string, tz?: string) => {
      const expr = buildCronExpr(preset, hour, day, custom ?? cronCustom);
      onChange({ ...config, cron_preset: preset, cron_hour: hour, cron_day: day, cron: expr, schedule_timezone: tz ?? scheduleTz });
    };

    return (
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Event</label>
          <Select value={event} onValueChange={(v) => {
            const updates: Record<string, unknown> = { ...config, event: v };
            // Auto-set browser timezone when switching to schedule for the first time
            if (v === "schedule" && !config.schedule_timezone) {
              updates.schedule_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
            }
            onChange(updates);
          }}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">manual – triggered manually</SelectItem>
              <SelectItem value="schedule">schedule – on a cron schedule</SelectItem>
              <SelectItem value="webhook">webhook – on HTTP call</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {event === "schedule" && (
          <div className="space-y-2.5 rounded-lg border bg-muted/30 p-3">
            {/* Frequency preset */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Frequency</label>
              <Select value={cronPreset} onValueChange={(v) => applyPreset(v, cronHour, cronDay)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Day-of-week picker (weekly only) */}
            {currentPreset.needsDay && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Day</label>
                <Select value={String(cronDay)} onValueChange={(v) => applyPreset(cronPreset, cronHour, Number(v))}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Hour picker (daily / weekly / monthly) */}
            {currentPreset.needsTime && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Run at (hour)</label>
                <Select value={String(cronHour)} onValueChange={(v) => applyPreset(cronPreset, Number(v), cronDay)}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {`${h.toString().padStart(2, "0")}:00`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Timezone picker */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Timezone</label>
              <Select value={scheduleTz} onValueChange={(v) => applyPreset(cronPreset, cronHour, cronDay, undefined, v)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">
                Schedule times are interpreted in this timezone
              </p>
            </div>

            {/* Custom cron expression input */}
            {cronPreset === "custom" && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Cron expression
                </label>
                <Input
                  className={`h-8 text-sm font-mono ${
                    cronCustom && !isValidCron(cronCustom) ? "border-red-400 focus-visible:ring-red-400" : ""
                  }`}
                  placeholder="*/15 * * * *"
                  value={cronCustom}
                  onChange={(e) => applyPreset("custom", cronHour, cronDay, e.target.value)}
                />
                {cronCustom && !isValidCron(cronCustom) ? (
                  <p className="text-[10px] text-red-600 font-medium">
                    Invalid cron expression — check the format: minute hour day-of-month month day-of-week
                  </p>
                ) : (
                  <p className="text-[10px] text-muted-foreground">
                    Format: minute hour day-of-month month day-of-week
                  </p>
                )}
              </div>
            )}

            {/* Human-readable summary + cron preview */}
            <div className="flex items-center gap-2 rounded-md bg-muted px-2.5 py-2">
              <Clock className="h-3 w-3 text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground flex-1">
                {describeCronPreset(cronPreset, cronHour, cronDay, cronCustom)}
                {" "}({scheduleTz.replace(/_/g, " ")})
              </span>
              {cronPreset !== "custom" && (
                <code className="text-[10px] font-mono text-muted-foreground/60 shrink-0">
                  {buildCronExpr(cronPreset, cronHour, cronDay, "")}
                </code>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (type === "http_request") {
    return (
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="w-28 shrink-0 space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Method</label>
            <Select value={String(config.method ?? "GET")} onValueChange={(v) => set("method", v)}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["GET", "POST", "PUT", "DELETE"].map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">URL</label>
            <AutocompleteInput
              className="h-8 text-sm"
              placeholder="https://api.example.com/endpoint"
              value={String(config.url ?? "")}
              onChange={(v) => set("url", v)}
              suggestions={suggestions}
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Headers (JSON)</label>
          <Textarea
            className="text-xs font-mono min-h-[52px] resize-none"
            placeholder='{"Authorization": "Bearer token"}'
            value={typeof config.headers === "object" ? JSON.stringify(config.headers, null, 2) : "{}"}
            onChange={(e) => {
              try {
                set("headers", JSON.parse(e.target.value));
              } catch {
                /* keep */
              }
            }}
          />
        </div>
        {["POST", "PUT"].includes(String(config.method)) && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Body (JSON)</label>
            <Textarea
              className="text-xs font-mono min-h-[52px] resize-none"
              placeholder='{"key": "value"}'
              value={typeof config.body === "object" ? JSON.stringify(config.body, null, 2) : "{}"}
              onChange={(e) => {
                try {
                  set("body", JSON.parse(e.target.value));
                } catch {
                  /* keep */
                }
              }}
            />
          </div>
        )}
        <VariableHelpBlock priorNodes={priorNodes} lastOutputs={lastOutputs} />
      </div>
    );
  }

  if (type === "run_query") {
    const queryText = String(config.query ?? "");
    const labelText = String(config.label ?? "");
    return (
      <RunQueryNodeForm
        queryText={queryText}
        queryName={config._queryName as string | undefined}
        labelText={labelText}
        selfId={selfId}
        onSetLabel={(v) => set("label", v)}
        onAddQuery={onAddQuery}
      />
    );
  }

  if (type === "condition") {
    // Support both new (branches[]) and legacy (expression) formats
    const rawBranches = config.branches as ConditionBranch[] | undefined;
    const branches: ConditionBranch[] =
      rawBranches && rawBranches.length > 0
        ? rawBranches
        : config.expression
        ? [{ id: "branch_1", label: "Branch 1", expression: String(config.expression) }]
        : [{ id: "branch_1", label: "Branch 1", expression: "" }];

    const setBranches = (updated: ConditionBranch[]) => onChange({ ...config, branches: updated, expression: undefined });

    const updateBranch = (idx: number, patch: Partial<ConditionBranch>) => {
      const next = branches.map((b, i) => (i === idx ? { ...b, ...patch } : b));
      setBranches(next);
    };

    const addBranch = () => {
      const newId = `branch_${branches.length + 1}`;
      setBranches([...branches, { id: newId, label: `Branch ${branches.length + 1}`, expression: "" }]);
    };

    const removeBranch = (idx: number) => {
      if (branches.length <= 1) return;
      setBranches(branches.filter((_, i) => i !== idx));
    };

    const fieldHints: Record<NodeType, string[]> = {
      trigger: ["event", "triggered_at", "data"],
      http_request: ["ok", "status", "status_text", "body"],
      run_query: ["rows", "row_count", "query", "label"],
      condition: ["passed", "matched_branch"],
      send_email: ["sent", "to", "subject", "message_id"],
      loop: ["rows", "row_count", "query", "label", "<column_name>"],
    };

    return (
      <div className="space-y-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Branches <span className="normal-case font-normal">(first match wins)</span>
            </label>
            <button
              type="button"
              onClick={addBranch}
              className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium"
            >
              <Plus className="h-3 w-3" /> Add Branch
            </button>
          </div>

          {branches.map((branch, idx) => (
            <div key={branch.id} className="rounded-lg border border-purple-100 bg-purple-50/40 p-2.5 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-purple-700 shrink-0 w-16">
                  {branch.id}
                </span>
                <Input
                  className="h-7 text-xs flex-1"
                  placeholder="Branch label (e.g. High Value)"
                  value={branch.label}
                  onChange={(e) => updateBranch(idx, { label: e.target.value })}
                />
                {branches.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeBranch(idx)}
                    className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <AutocompleteInput
                className="h-7 text-xs font-mono"
                placeholder="run_query_1.row_count > 0"
                value={branch.expression}
                onChange={(v) => updateBranch(idx, { expression: v })}
                suggestions={suggestions}
              />
            </div>
          ))}

          <div className="rounded-lg border border-dashed border-muted-foreground/30 px-2.5 py-2 text-xs text-muted-foreground">
            <span className="font-medium">default</span> — runs when no branch above matches
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Format: <code className="bg-muted px-1 rounded">node_id.field op value</code>
          &nbsp;· Operators: <code className="bg-muted px-1 rounded">== != &gt; &lt; &gt;= &lt;=</code>
        </p>

        {priorNodes.length > 0 && (
          <div className="rounded-md bg-muted/50 p-2 space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Available fields from prior nodes:</p>
            {priorNodes.map((n) => (
              <div key={n.id} className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{n.id}</span>:{" "}
                {(fieldHints[n.type] ?? []).map((f) => `${n.id}.${f}`).join(", ")}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (type === "send_email") {
    // Find query nodes for quick-insert format buttons
    const queryNodes = priorNodes.filter((n) => n.type === "run_query");

    const insertFormat = (nodeId: string, format: string) => {
      const current = String(config.body ?? "");
      const token = `{{${nodeId}.rows | ${format}}}`;
      onChange({ ...config, body: current ? `${current}\n${token}` : token });
    };

    return (
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">To</label>
          <AutocompleteInput
            className="h-8 text-sm"
            placeholder="recipient@example.com or {{run_query_1.rows[0].email}}"
            value={String(config.to ?? "")}
            onChange={(v) => set("to", v)}
            suggestions={suggestions}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Subject</label>
          <AutocompleteInput
            className="h-8 text-sm"
            placeholder="Email subject – use {{node_id.field}}"
            value={String(config.subject ?? "")}
            onChange={(v) => set("subject", v)}
            suggestions={suggestions}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Body</label>
          <AutocompleteInput
            className="text-sm min-h-[72px] resize-none"
            placeholder="Email body – use {{node_id.rows | table}} to format data"
            value={String(config.body ?? "")}
            onChange={(v) => set("body", v)}
            suggestions={suggestions}
            multiline
          />
        </div>

        {/* Format operator quick-insert */}
        {queryNodes.length > 0 && (
          <div className="rounded-md border bg-muted/50 p-2.5 space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              📊 Insert formatted data from query results:
            </p>
            {queryNodes.map((qn) => (
              <div key={qn.id} className="space-y-1">
                <span className="text-xs font-medium text-foreground">{qn.id}</span>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: "table", label: "📋 Table", desc: "HTML table" },
                    { id: "list", label: "📝 List", desc: "Bulleted list" },
                    { id: "csv", label: "📄 CSV", desc: "Comma-separated" },
                    { id: "summary", label: "📊 Summary", desc: "Row count + preview" },
                  ].map((fmt) => (
                    <button
                      key={fmt.id}
                      type="button"
                      onClick={() => insertFormat(qn.id, fmt.id)}
                      className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs font-medium hover:bg-accent transition-colors"
                      title={fmt.desc}
                    >
                      {fmt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground/70 mt-1">
              Syntax: <code className="bg-muted px-1 rounded">{"{{node_id.rows | table}}"}</code> — 
              Formats: <code className="bg-muted px-1 rounded">table</code>{" "}
              <code className="bg-muted px-1 rounded">list</code>{" "}
              <code className="bg-muted px-1 rounded">csv</code>{" "}
              <code className="bg-muted px-1 rounded">summary</code>
            </p>
          </div>
        )}

        <VariableHelpBlock priorNodes={priorNodes} lastOutputs={lastOutputs} />
      </div>
    );
  }

  if (type === "loop") {
    const queryText = String(config.query ?? "");
    const labelText = String(config.label ?? "");
    const isParallel = config.parallel !== false;
    const maxConcurrency = Number(config.max_concurrency ?? 5);

    return (
      <div className="space-y-3">
        <RunQueryNodeForm
          queryText={queryText}
          queryName={config._queryName as string | undefined}
          labelText={labelText}
          selfId={selfId}
          onSetLabel={(v) => set("label", v)}
          onAddQuery={onAddQuery}
        />

        <div className="rounded-lg border bg-orange-50/50 p-3 space-y-3">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={isParallel}
                onChange={(e) => set("parallel", e.target.checked)}
                className="rounded border-gray-300"
              />
              Run iterations in parallel
            </label>
          </div>

          {isParallel && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Max Concurrency
              </label>
              <Select
                value={String(maxConcurrency)}
                onValueChange={(v) => set("max_concurrency", Number(v))}
              >
                <SelectTrigger className="h-8 text-sm w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 5, 10, 20, 50].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Maximum number of rows processed simultaneously.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-md bg-muted/50 border p-2.5 space-y-1">
          <p className="text-xs font-medium text-muted-foreground">
            Downstream nodes with this loop as source will run once per row.
            Row columns are available as placeholders:
          </p>
          <p className="text-xs text-muted-foreground">
            <code className="bg-muted px-1 rounded">{`{{${selfId}.column_name}}`}</code> —
            resolves to each row's column value during iteration.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

// ────────────────────────────────────────────────────────────
// Workflow Builder Dialog
// ────────────────────────────────────────────────────────────
function WorkflowBuilderDialog({
  open,
  onClose,
  onSaved,
  editing,
  restoredDraft,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing: Workflow | null;
  restoredDraft?: WorkflowDraft | null;
}) {
  const { profile } = useAuth();
  const { log: auditLog } = useAuditLog();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [departmentId, setDepartmentId] = useState<string>("none");
  const [departments, setDepartments] = useState<Department[]>([]);
  const [showCreateDept, setShowCreateDept] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const [newDeptAiName, setNewDeptAiName] = useState("");
  const [creatingDept, setCreatingDept] = useState(false);
  const [nodes, setNodes] = useState<WorkflowNode[]>([
    { id: "trigger_1", type: "trigger", depends_on: [], config: defaultConfig("trigger") },
  ]);
  const [saving, setSaving] = useState(false);
  const [lastOutputs, setLastOutputs] = useState<Record<string, unknown>>({});
  const [editorNodeId, setEditorNodeId] = useState<string | null>(null);
  const [editorQueryData, setEditorQueryData] = useState<Record<string, any> | null>(null);
  const [debugOpen, setDebugOpen] = useState(false);

  // Fetch last execution's node outputs for variable hints
  useEffect(() => {
    if (!open || !editing) {
      setLastOutputs({});
      return;
    }
    (async () => {
      // Get the most recent successful execution
      const { data: execs } = await supabase
        .from("tasks")
        .select("id")
        .eq("workflow_id", editing.id)
        .eq("status", "success")
        .order("created_at", { ascending: false })
        .limit(1);
      if (!execs || execs.length === 0) return;

      const { data: nodeExecData } = await supabase
        .from("task_steps")
        .select("node_id, output")
        .eq("task_id", execs[0].id)
        .eq("status", "success");
      if (!nodeExecData) return;

      const outputs: Record<string, unknown> = {};
      for (const ne of nodeExecData) {
        if (ne.output) outputs[ne.node_id] = ne.output;
      }
      setLastOutputs(outputs);
    })();
  }, [open, editing]);

  // Fetch departments when dialog opens
  useEffect(() => {
    if (!open || !profile?.client_id) return;
    (async () => {
      const { data } = await supabase
        .from("department")
        .select("id, name")
        .eq("client_id", profile.client_id)
        .order("name");
      if (data) setDepartments(data as Department[]);
    })();
  }, [open, profile?.client_id]);

  // Reset state when dialog opens/closes or editing target changes
  useEffect(() => {
    if (!open) return;
    if (restoredDraft) {
      setName(restoredDraft.name);
      setDescription(restoredDraft.description);
      setDepartmentId("none");
      setNodes(
        restoredDraft.nodes.length > 0
          ? restoredDraft.nodes
          : [{ id: "trigger_1", type: "trigger", depends_on: [], config: defaultConfig("trigger") }],
      );
    } else if (editing) {
      setName(editing.name);
      setDescription(editing.description ?? "");
      setDepartmentId(editing.department_id ?? "none");
      setNodes(
        editing.definition.nodes.length > 0
          ? editing.definition.nodes
          : [{ id: "trigger_1", type: "trigger", depends_on: [], config: defaultConfig("trigger") }],
      );
    } else {
      setName("");
      setDescription("");
      setDepartmentId("none");
      setNodes([{ id: "trigger_1", type: "trigger", depends_on: [], config: defaultConfig("trigger") }]);
    }
  }, [open, editing, restoredDraft]);

  const addNode = (type: NodeType) => {
    const count = nodes.filter((n) => n.type === type).length;
    const newId = `${type}_${count + 1}`;
    const last = nodes[nodes.length - 1];

    const newNode: WorkflowNode = {
      id: newId,
      type,
      depends_on: last ? [last.id] : [],
      config: defaultConfig(type),
    };

    // Auto-attach to the first branch when added right after a condition node
    if (last?.type === "condition") {
      const branches = last.config.branches as ConditionBranch[] | undefined;
      const firstBranch = branches?.[0];
      if (firstBranch) {
        newNode.condition_branch = { condition_node_id: last.id, branch_id: firstBranch.id };
      }
    }

    // Auto-attach loop_source when added right after a loop node
    if (last?.type === "loop") {
      newNode.loop_source = last.id;
    }

    setNodes((prev) => [...prev, newNode]);
  };

  // Insert a node at a specific position (between existing nodes).
  // Rewires depends_on so the chain stays intact.
  const insertNodeAt = (type: NodeType, position: number) => {
    // Generate a unique id
    const existingIds = new Set(nodes.map((n) => n.id));
    let count = 1;
    while (existingIds.has(`${type}_${count}`)) count++;
    const newId = `${type}_${count}`;

    setNodes((prev) => {
      const before = prev[position - 1]; // node above (may be undefined if position=0)
      const after = prev[position];       // node below (may be undefined)

      const newNode: WorkflowNode = {
        id: newId,
        type,
        depends_on: before ? [before.id] : [],
        config: defaultConfig(type),
      };

      // Auto-attach condition branch if inserting after a condition
      if (before?.type === "condition") {
        const branches = before.config.branches as ConditionBranch[] | undefined;
        const firstBranch = branches?.[0];
        if (firstBranch) {
          newNode.condition_branch = { condition_node_id: before.id, branch_id: firstBranch.id };
        }
      }

      // Auto-attach loop_source if inserting after a loop
      if (before?.type === "loop") {
        newNode.loop_source = before.id;
      }

      // Rewire: if the node below depended on the node above, point it to the new node
      const updated = prev.map((n) => {
        if (n === after && before && n.depends_on.includes(before.id)) {
          return { ...n, depends_on: n.depends_on.map((d) => (d === before.id ? newId : d)) };
        }
        return n;
      });

      // Splice the new node in
      const result = [...updated];
      result.splice(position, 0, newNode);
      return result;
    });
  };

  const [insertMenuIdx, setInsertMenuIdx] = useState<number | null>(null);

  const removeNode = (id: string) =>
    setNodes((prev) =>
      prev
        .filter((n) => n.id !== id)
        .map((n) => ({
          ...n,
          depends_on: n.depends_on.filter((d) => d !== id),
          // Clear condition_branch if it referenced the removed node
          condition_branch:
            n.condition_branch?.condition_node_id === id ? undefined : n.condition_branch,
          // Clear loop_source if it referenced the removed node
          loop_source: n.loop_source === id ? undefined : n.loop_source,
        })),
    );

  const updateConfig = (id: string, cfg: Record<string, unknown>) =>
    setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, config: cfg } : n)));

  const updateLabel = (id: string, label: string) =>
    setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, label: label || undefined } : n)));

  const updateConditionBranch = (
    nodeId: string,
    branch: WorkflowNode["condition_branch"]
  ) =>
    setNodes((prev) => prev.map((n) => (n.id === nodeId ? { ...n, condition_branch: branch } : n)));

  const updateLoopSource = (nodeId: string, loopSource: string | undefined) =>
    setNodes((prev) => prev.map((n) => (n.id === nodeId ? { ...n, loop_source: loopSource } : n)));

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Workflow name is required");
      return;
    }
    if (!profile?.client_id) {
      toast.error("No client associated with your account");
      return;
    }

    setSaving(true);
    try {
      const definition: WorkflowDefinition = { nodes };

      // Client-side structural check before hitting the edge function
      if (nodes.length === 0 || nodes[0].type !== "trigger") {
        toast.error("First node must be a Trigger");
        setSaving(false);
        return;
      }

      const ids = nodes.map((n) => n.id);
      if (new Set(ids).size !== ids.length) {
        toast.error("Node IDs must be unique");
        setSaving(false);
        return;
      }

      // Extract cron expression and timezone from trigger node (only when event === "schedule")
      const triggerNode = nodes[0];
      const isScheduled = triggerNode?.config?.event === "schedule";
      const cronExpression = isScheduled
        ? (triggerNode.config.cron as string | undefined) ?? null
        : null;
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
      const scheduleTz = isScheduled
        ? (triggerNode.config.schedule_timezone as string | undefined) ?? browserTz
        : "UTC";

      // next_run_at is left null — the scheduler edge function (runs every minute)
      // will compute the first occurrence with proper timezone-aware croner.

      const effectiveEditingId = editing?.id || restoredDraft?.editingId;
      if (effectiveEditingId) {
        const { error } = await supabase
          .from("workflows")
          .update({
            name: name.trim(),
            description: description.trim() || null,
            definition: definition as unknown as Json,
            updated_at: new Date().toISOString(),
            cron_expression: cronExpression,
            schedule_timezone: scheduleTz,
            next_run_at: null,
            department_id: departmentId === "none" ? null : departmentId,
          })
          .eq("id", effectiveEditingId);
        if (error) throw error;
        auditLog({ action: "edited", resourceType: "workflow", resourceId: effectiveEditingId, resourceName: name.trim() });
        toast.success("Workflow updated");
      } else {
        const { error } = await supabase
          .from("workflows")
          .insert({
            name: name.trim(),
            description: description.trim() || null,
            definition: definition as unknown as Json,
            client_id: profile.client_id,
            cron_expression: cronExpression,
            schedule_timezone: scheduleTz,
            next_run_at: null,
            department_id: departmentId === "none" ? null : departmentId,
          });
        if (error) throw error;
        auditLog({ action: "created", resourceType: "workflow", resourceName: name.trim() });
        toast.success("Workflow created");
      }

      onSaved();
      onClose();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to save workflow");
    } finally {
      setSaving(false);
    }
  };

  // Open query editor for the given node – edit existing or create new
  const handleAddQuery = (nodeId: string) => {
    const node = nodes.find((n) => n.id === nodeId);
    setEditorNodeId(nodeId);
    const rawSql = (node?.config?.query as string) || "";
    const queryName = (node?.config?._queryName as string) || "";
    if (rawSql) {
      // Build a synthetic editingQuery from the node's inline SQL
      setEditorQueryData({
        sql_query: rawSql,
        name: queryName,
        generation_mode: "manual",
      });
    } else {
      // No query yet – open blank editor
      setEditorQueryData(null);
    }
  };

  const handleQuerySaved = (query: { id: string; name: string; sql_query: string }) => {
    if (!editorNodeId) return;
    setNodes((prev) =>
      prev.map((n) =>
        n.id === editorNodeId
          ? {
              ...n,
              config: {
                ...n.config,
                _queryName: query.name || undefined,
                query: query.sql_query,
                label: n.config.label || query.name || undefined,
              },
            }
          : n
      )
    );
  };

  const handleEditorClose = () => {
    setEditorNodeId(null);
    setEditorQueryData(null);
  };

  // Buttons for adding non-trigger nodes
  const addableTypes: NodeType[] = ["http_request", "run_query", "condition", "send_email", "loop"];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Fixed height so ScrollArea gets a concrete container */}
      <DialogContent className="max-w-2xl h-[92vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="text-base">{editing || restoredDraft?.editingId ? "Edit Workflow" : "New Workflow"}</DialogTitle>
        </DialogHeader>

        {/* Scrollable body — overflow-y-auto on a flex-1 child with min-h-0 */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
          {/* ── Metadata ── */}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Workflow Name</label>
              <Input placeholder="e.g. Daily Report Mailer" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">
                Description <span className="font-normal">(optional)</span>
              </label>
              <Textarea
                className="resize-none min-h-[56px] text-sm"
                placeholder="What does this workflow do?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">
                Department <span className="font-normal">(optional)</span>
              </label>
              <div className="flex items-center gap-2">
                <Select value={departmentId} onValueChange={setDepartmentId}>
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No department</SelectItem>
                    {departments.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1 shrink-0 text-xs h-9"
                  onClick={() => { setNewDeptName(""); setNewDeptAiName(""); setShowCreateDept(true); }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  New
                </Button>
              </div>
            </div>
          </div>

          <Separator />

          {/* ── Nodes ── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">
                Nodes
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">({nodes.length})</span>
              </span>
              <span className="text-xs text-muted-foreground">First node must be a Trigger</span>
            </div>

            {/* Node list */}
            <div className="space-y-0">
              {nodes.map((node, idx) => {
                const meta = NODE_META[node.type];
                const Icon = meta.icon;
                return (
                  <div key={node.id}>
                    {/* Node card */}
                    <div className={`rounded-xl border ${meta.bg} p-4`}>
                      <div className="flex items-start gap-3">
                        {/* Icon badge */}
                        <div className={`rounded-lg p-2 bg-white border ${meta.bg} shrink-0 shadow-sm`}>
                          <Icon className={`h-4 w-4 ${meta.color}`} />
                        </div>

                        <div className="flex-1 min-w-0 space-y-3">
                          {/* Header row */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold">{meta.label}</span>
                            <code className="text-xs bg-white/70 border px-1.5 py-0.5 rounded text-muted-foreground">
                              {node.id}
                            </code>
                            <input
                              type="text"
                              className="text-xs bg-white/70 border rounded px-1.5 py-0.5 w-28 placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
                              placeholder="Label…"
                              value={node.label ?? ""}
                              onChange={(e) => updateLabel(node.id, e.target.value)}
                            />
                            {node.depends_on.length > 0 && (
                              <span className="text-xs text-muted-foreground">← {node.depends_on.join(", ")}</span>
                            )}
                            {node.condition_branch && (
                              <span className="text-xs bg-purple-100 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded">
                                branch: {node.condition_branch.branch_id}
                              </span>
                            )}
                            {node.loop_source && (
                              <span className="text-xs bg-orange-100 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                                <Repeat className="h-3 w-3" />
                                loop: {node.loop_source}
                              </span>
                            )}
                          </div>

                          {/* Loop source selector – shown when a loop node exists upstream */}
                          {(() => {
                            // Only show for non-trigger, non-loop nodes
                            if (node.type === "trigger" || node.type === "loop") return null;
                            // Collect all loop nodes that appear before this node in the list
                            const loopNodes = nodes.filter(
                              (n) => n.type === "loop" && n.id !== node.id
                            );
                            if (loopNodes.length === 0) return null;

                            return (
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                  Loop Source
                                </label>
                                <Select
                                  value={node.loop_source ?? "none"}
                                  onValueChange={(v) =>
                                    updateLoopSource(node.id, v === "none" ? undefined : v)
                                  }
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue placeholder="Not in a loop" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">
                                      Not in a loop
                                    </SelectItem>
                                    {loopNodes.map((ln) => (
                                      <SelectItem key={ln.id} value={ln.id}>
                                        {ln.id} — runs once per row
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                          })()}

                          {/* Branch selector – shown when this node depends on a condition node */}
                          {(() => {
                            const condDep = node.depends_on
                              .map((depId) => nodes.find((n) => n.id === depId))
                              .find((n) => n?.type === "condition");
                            if (!condDep) return null;

                            const condBranches =
                              (condDep.config.branches as ConditionBranch[] | undefined) ?? [];

                            return (
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                  Execute on Branch
                                </label>
                                <Select
                                  value={node.condition_branch?.branch_id ?? ""}
                                  onValueChange={(v) =>
                                    updateConditionBranch(node.id, {
                                      condition_node_id: condDep.id,
                                      branch_id: v,
                                    })
                                  }
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue placeholder="Select a branch…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {condBranches.map((b) => (
                                      <SelectItem key={b.id} value={b.id}>
                                        {b.label || b.id}
                                      </SelectItem>
                                    ))}
                                    <SelectItem value="default">
                                      default — no branch matched
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                          })()}

                          {/* Config form */}
                          <NodeConfigForm
                            type={node.type}
                            config={node.config}
                            onChange={(cfg) => updateConfig(node.id, cfg)}
                            allNodes={nodes}
                            selfId={node.id}
                            onAddQuery={node.type === "run_query" || node.type === "loop" ? () => handleAddQuery(node.id) : undefined}
                            lastOutputs={lastOutputs}
                          />
                        </div>

                        {/* Remove button (not for trigger at idx 0) */}
                        {idx > 0 && (
                          <button
                            type="button"
                            className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-destructive hover:bg-white/60 transition-colors"
                            onClick={() => removeNode(node.id)}
                            title="Remove node"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Connector with insert button between nodes */}
                    {idx < nodes.length - 1 && (
                      <div className="flex justify-center py-1 relative group/connector">
                        {insertMenuIdx === idx + 1 ? (
                          <div className="flex items-center gap-1.5 py-1">
                            {addableTypes.map((t) => {
                              const m = NODE_META[t];
                              const BtnIcon = m.icon;
                              return (
                                <button
                                  key={t}
                                  type="button"
                                  title={m.label}
                                  onClick={() => { insertNodeAt(t, idx + 1); setInsertMenuIdx(null); }}
                                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors hover:shadow-sm bg-white ${m.bg}`}
                                >
                                  <BtnIcon className={`h-3 w-3 ${m.color}`} />
                                  {m.label}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => setInsertMenuIdx(null)}
                              className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setInsertMenuIdx(idx + 1)}
                            className="flex items-center justify-center h-5 w-5 rounded-full border border-dashed border-muted-foreground/30 text-muted-foreground/40 hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors opacity-0 group-hover/connector:opacity-100 focus:opacity-100"
                            title="Insert node here"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Add node palette */}
            <div className="rounded-xl border border-dashed bg-muted/30 p-4 space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Add a node</p>
              <div className="flex flex-wrap gap-2">
                {addableTypes.map((type) => {
                  const meta = NODE_META[type];
                  const Icon = meta.icon;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => addNode(type)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors hover:shadow-sm bg-white ${meta.bg}`}
                    >
                      <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t shrink-0 flex items-center justify-between gap-3">
          <Button variant="outline" size="sm" onClick={() => setDebugOpen(true)} disabled={saving || nodes.length < 2}>
            <Bug className="h-3.5 w-3.5 mr-1" /> Debug
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save Changes" : "Create Workflow"}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* ── Inline Query Editor dialog ───────────────────────────── */}
      <Dialog
        open={!!editorNodeId}
        onOpenChange={(o) => { if (!o) handleEditorClose(); }}
      >
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 overflow-hidden flex flex-col">
          {editorNodeId && (
            <QueryEditor
              editingQuery={editorQueryData}
              onClose={handleEditorClose}
              onSaved={handleQuerySaved}
              saveToDb={false}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ── Create Department dialog ─────────────────────────────── */}
      <Dialog open={showCreateDept} onOpenChange={setShowCreateDept}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Department</DialogTitle>
            <DialogDescription>Add a new department to your organization.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Department Name</label>
              <Input
                placeholder="e.g. Sales, Engineering"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                disabled={creatingDept}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">AI Employee Name <span className="font-normal text-muted-foreground">(optional)</span></label>
              <Input
                placeholder="e.g. Sales Assistant"
                value={newDeptAiName}
                onChange={(e) => setNewDeptAiName(e.target.value)}
                disabled={creatingDept}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDept(false)} disabled={creatingDept}>Cancel</Button>
            <Button
              disabled={!newDeptName.trim() || creatingDept}
              onClick={async () => {
                if (!profile?.client_id || !newDeptName.trim()) return;
                setCreatingDept(true);
                try {
                  const { data: inserted, error } = await supabase
                    .from("department")
                    .insert({ name: newDeptName.trim(), ai_employee_name: newDeptAiName.trim() || null, client_id: profile.client_id })
                    .select("id, name")
                    .single();
                  if (error) throw new Error(error.message);
                  toast.success(`Department "${newDeptName.trim()}" created`);
                  if (inserted) {
                    setDepartments((prev) => [...prev, inserted as Department].sort((a, b) => a.name.localeCompare(b.name)));
                    setDepartmentId((inserted as Department).id);
                  }
                  setShowCreateDept(false);
                } catch (err: any) {
                  toast.error(err.message);
                }
                setCreatingDept(false);
              }}
            >
              {creatingDept && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Debug Panel ────────────────────────────────────────────── */}
      <WorkflowDebugPanel
        nodes={nodes}
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        onOutputsChange={(outputs) => setLastOutputs((prev) => ({ ...prev, ...outputs }))}
      />
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────
// Execution History Dialog
// ────────────────────────────────────────────────────────────
function ExecutionHistoryDialog({
  workflow,
  open,
  onClose,
}: {
  workflow: Workflow;
  open: boolean;
  onClose: () => void;
}) {
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExec, setSelectedExec] = useState<AllExecution | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("tasks")
      .select("*")
      .eq("workflow_id", workflow.id)
      .order("created_at", { ascending: false })
      .limit(20);
    if (data) setExecutions(data as WorkflowExecution[]);
    setLoading(false);
  }, [workflow.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const dur = (s: string | null, e: string | null) => {
    if (!s || !e) return "";
    const ms = new Date(e).getTime() - new Date(s).getTime();
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="text-base">
            Execution History – <span className="font-normal text-muted-foreground">{toCamelCase(workflow.name)}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : executions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-6">
              <Clock className="h-8 w-8 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No executions yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Run this workflow to see history here</p>
            </div>
          ) : (
            <div className="divide-y">
              {executions.map((exec) => (
                <div key={exec.id} className="px-6 py-4 hover:bg-muted/20 transition-colors">
                  <button className="w-full text-left" onClick={() => setSelectedExec({ ...exec, workflow_name: workflow.name })}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="flex items-center gap-2">
                            {exec.tid && (
                              <span className="text-xs font-mono font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                                {exec.tid}
                              </span>
                            )}
                            <p className="text-sm font-medium">{new Date(exec.created_at).toLocaleString()}</p>
                          </div>
                          {exec.error && (
                            <p className="text-xs text-destructive mt-0.5 max-w-xs truncate">{exec.error}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {exec.started_at && exec.completed_at && (
                          <span className="text-xs text-muted-foreground">
                            {dur(exec.started_at, exec.completed_at)}
                          </span>
                        )}
                        <StatusBadge status={exec.status} />
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t shrink-0 flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <TaskDetailDialog
      open={!!selectedExec}
      onClose={() => setSelectedExec(null)}
      execution={selectedExec}
    />
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Workflow Debug Panel – step-through execution with variable inspector
// ────────────────────────────────────────────────────────────

interface DebugNodeState {
  status: ExecStatus;
  output?: Record<string, unknown>;
  resolvedConfig?: Record<string, unknown>;
  error?: string;
  durationMs?: number;
}

function topoSort(nodes: WorkflowNode[]): WorkflowNode[] {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const visited = new Set<string>();
  const inStack = new Set<string>();
  const order: WorkflowNode[] = [];
  function dfs(id: string) {
    if (visited.has(id)) return;
    if (inStack.has(id)) return; // cycle – skip
    inStack.add(id);
    const node = nodeMap.get(id);
    if (!node) return;
    for (const dep of node.depends_on) dfs(dep);
    inStack.delete(id);
    visited.add(id);
    order.push(node);
  }
  for (const n of nodes) dfs(n.id);
  return order;
}

function WorkflowDebugPanel({
  nodes,
  open,
  onClose,
  onOutputsChange,
}: {
  nodes: WorkflowNode[];
  open: boolean;
  onClose: () => void;
  onOutputsChange?: (outputs: Record<string, unknown>) => void;
}) {
  const [sortedNodes, setSortedNodes] = useState<WorkflowNode[]>([]);
  const [nodeStates, setNodeStates] = useState<Record<string, DebugNodeState>>({});
  const [nodeOutputs, setNodeOutputs] = useState<Record<string, unknown>>({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [stepping, setStepping] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [triggerData, setTriggerData] = useState("{}");
  const [evalExpr, setEvalExpr] = useState("");
  const [evalResult, setEvalResult] = useState<{ value: unknown; error?: string } | null>(null);
  const [evalHistory, setEvalHistory] = useState<{ expr: string; value: unknown; error?: string }[]>([]);

  // Resolve a dot/bracket path like "run_query_1.rows[0].customer_id"
  const resolveExpression = useCallback(
    (expr: string): { value: unknown; error?: string } => {
      const trimmed = expr.trim();
      if (!trimmed) return { value: undefined, error: "Empty expression" };

      // Tokenize: split on dots and bracket accessors
      // e.g. "run_query_1.rows[0].name" → ["run_query_1", "rows", "0", "name"]
      const parts: string[] = [];
      let current = "";
      for (let i = 0; i < trimmed.length; i++) {
        const ch = trimmed[i];
        if (ch === ".") {
          if (current) parts.push(current);
          current = "";
        } else if (ch === "[") {
          if (current) parts.push(current);
          current = "";
          // read until ]
          let bracket = "";
          i++;
          while (i < trimmed.length && trimmed[i] !== "]") {
            bracket += trimmed[i];
            i++;
          }
          // strip quotes if present (e.g. ["key"])
          parts.push(bracket.replace(/^["']|["']$/g, ""));
        } else {
          current += ch;
        }
      }
      if (current) parts.push(current);

      if (parts.length === 0) return { value: undefined, error: "Invalid path" };

      // Walk the nodeOutputs tree
      let cursor: unknown = nodeOutputs;
      for (let i = 0; i < parts.length; i++) {
        if (cursor === null || cursor === undefined) {
          return { value: undefined, error: `Cannot read "${parts[i]}" – parent is ${cursor}` };
        }
        if (typeof cursor === "object") {
          if (Array.isArray(cursor)) {
            const idx = parseInt(parts[i], 10);
            if (isNaN(idx)) return { value: undefined, error: `"${parts[i]}" is not a valid array index` };
            cursor = cursor[idx];
          } else {
            cursor = (cursor as Record<string, unknown>)[parts[i]];
          }
        } else {
          return { value: undefined, error: `Cannot access "${parts[i]}" on ${typeof cursor}` };
        }
      }
      return { value: cursor };
    },
    [nodeOutputs]
  );

  const handleEvaluate = () => {
    const result = resolveExpression(evalExpr);
    setEvalResult(result);
    setEvalHistory((prev) => [{ expr: evalExpr, ...result }, ...prev].slice(0, 20));
  };

  // Propagate live outputs back to the builder for {{ autocomplete in config fields
  useEffect(() => {
    if (Object.keys(nodeOutputs).length > 0) onOutputsChange?.(nodeOutputs);
  }, [nodeOutputs, onOutputsChange]);

  // Sort nodes on open
  useEffect(() => {
    if (!open) return;
    // Filter out loop-scoped nodes (they run inside loops, not standalone)
    const loopScoped = new Set(nodes.filter((n) => n.loop_source).map((n) => n.id));
    const mainNodes = nodes.filter((n) => !loopScoped.has(n.id));
    const sorted = topoSort(mainNodes);
    setSortedNodes(sorted);
    setNodeStates({});
    setNodeOutputs({});
    setCurrentIdx(0);
    setExpandedNodes(new Set());
  }, [open, nodes]);

  const reset = () => {
    setNodeStates({});
    setNodeOutputs({});
    setCurrentIdx(0);
    setExpandedNodes(new Set());
    setEvalExpr("");
    setEvalResult(null);
    setEvalHistory([]);
  };

  const stepOne = async () => {
    if (currentIdx >= sortedNodes.length) return;
    const node = sortedNodes[currentIdx];
    setStepping(true);

    // Mark as running
    setNodeStates((prev) => ({ ...prev, [node.id]: { status: "running" } }));

    let parsedTriggerData: Record<string, unknown> | undefined;
    try {
      const parsed = JSON.parse(triggerData);
      if (typeof parsed === "object" && parsed !== null) parsedTriggerData = parsed;
    } catch { /* ignore */ }

    const startTime = Date.now();
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();
      const res = await fetch(`${supabaseUrl}/functions/v1/workflow-runner/debug-step`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          node,
          node_outputs: nodeOutputs,
          trigger_data: parsedTriggerData,
        }),
      });
      const data = await res.json();
      const durationMs = Date.now() - startTime;

      if (data.success) {
        const newOutputs = { ...nodeOutputs, [node.id]: data.output };
        setNodeOutputs(newOutputs);
        setNodeStates((prev) => ({
          ...prev,
          [node.id]: {
            status: "success",
            output: data.output,
            resolvedConfig: data.resolved_config,
            durationMs,
          },
        }));
        setExpandedNodes((prev) => new Set(prev).add(node.id));
        setCurrentIdx((i) => i + 1);
      } else {
        setNodeStates((prev) => ({
          ...prev,
          [node.id]: {
            status: "failed",
            error: data.error,
            durationMs,
          },
        }));
        setExpandedNodes((prev) => new Set(prev).add(node.id));
      }
    } catch (err: unknown) {
      setNodeStates((prev) => ({
        ...prev,
        [node.id]: {
          status: "failed",
          error: err instanceof Error ? err.message : String(err),
          durationMs: Date.now() - startTime,
        },
      }));
      setExpandedNodes((prev) => new Set(prev).add(node.id));
    } finally {
      setStepping(false);
    }
  };

  const runAll = async () => {
    let idx = currentIdx;
    while (idx < sortedNodes.length) {
      const node = sortedNodes[idx];

      setStepping(true);
      setNodeStates((prev) => ({ ...prev, [node.id]: { status: "running" } }));

      let parsedTriggerData: Record<string, unknown> | undefined;
      try {
        const parsed = JSON.parse(triggerData);
        if (typeof parsed === "object" && parsed !== null) parsedTriggerData = parsed;
      } catch { /* ignore */ }

      const startTime = Date.now();
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const headers = await buildEdgeFunctionHeaders();
        // Get fresh outputs from the latest state
        const currentOutputs = await new Promise<Record<string, unknown>>((resolve) => {
          setNodeOutputs((prev) => { resolve(prev); return prev; });
        });

        const res = await fetch(`${supabaseUrl}/functions/v1/workflow-runner/debug-step`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            node,
            node_outputs: currentOutputs,
            trigger_data: parsedTriggerData,
          }),
        });
        const data = await res.json();
        const durationMs = Date.now() - startTime;

        if (data.success) {
          setNodeOutputs((prev) => ({ ...prev, [node.id]: data.output }));
          setNodeStates((prev) => ({
            ...prev,
            [node.id]: {
              status: "success",
              output: data.output,
              resolvedConfig: data.resolved_config,
              durationMs,
            },
          }));
          setExpandedNodes((prev) => new Set(prev).add(node.id));
          idx++;
          setCurrentIdx(idx);
        } else {
          setNodeStates((prev) => ({
            ...prev,
            [node.id]: { status: "failed", error: data.error, durationMs },
          }));
          setExpandedNodes((prev) => new Set(prev).add(node.id));
          setStepping(false);
          return; // stop on failure
        }
      } catch (err: unknown) {
        setNodeStates((prev) => ({
          ...prev,
          [node.id]: {
            status: "failed",
            error: err instanceof Error ? err.message : String(err),
            durationMs: Date.now() - startTime,
          },
        }));
        setExpandedNodes((prev) => new Set(prev).add(node.id));
        setStepping(false);
        return;
      }
    }
    setStepping(false);
  };

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const allDone = currentIdx >= sortedNodes.length;
  const hasFailed = Object.values(nodeStates).some((s) => s.status === "failed");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="text-base flex items-center gap-2">
            <Bug className="h-4 w-4" />
            Step-through Debugger
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          {/* Trigger data input */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Trigger Data (JSON)
            </label>
            <Textarea
              className="font-mono text-xs resize-none min-h-[48px] max-h-[80px]"
              placeholder='{"key": "value"}'
              value={triggerData}
              onChange={(e) => setTriggerData(e.target.value)}
              disabled={currentIdx > 0}
            />
          </div>

          <Separator />

          {/* Node list */}
          <div className="space-y-2">
            {sortedNodes.map((node, idx) => {
              const meta = NODE_META[node.type];
              const Icon = meta.icon;
              const state = nodeStates[node.id];
              const isExpanded = expandedNodes.has(node.id);
              const isCurrent = idx === currentIdx && !allDone && !hasFailed;

              return (
                <div
                  key={node.id}
                  className={`rounded-lg border transition-colors ${
                    isCurrent
                      ? "border-blue-300 bg-blue-50/50 ring-1 ring-blue-200"
                      : state?.status === "success"
                      ? "border-green-200 bg-green-50/30"
                      : state?.status === "failed"
                      ? "border-red-200 bg-red-50/30"
                      : state?.status === "running"
                      ? "border-blue-200 bg-blue-50/30"
                      : "border-border bg-muted/20"
                  }`}
                >
                  {/* Header */}
                  <button
                    className="w-full text-left px-3 py-2.5 flex items-center gap-2"
                    onClick={() => state && toggleNode(node.id)}
                    disabled={!state}
                  >
                    <div className={`rounded p-1 bg-white border ${meta.bg} shrink-0`}>
                      <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{node.label || node.id}</span>
                        {node.label && <code className="text-[10px] text-muted-foreground">{node.id}</code>}
                        <span className="text-xs text-muted-foreground">({meta.label})</span>
                        {isCurrent && !state && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">next</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {state?.durationMs !== undefined && (
                        <span className="text-xs text-muted-foreground">
                          {state.durationMs < 1000 ? `${state.durationMs}ms` : `${(state.durationMs / 1000).toFixed(1)}s`}
                        </span>
                      )}
                      {state ? (
                        <StatusBadge status={state.status} />
                      ) : (
                        <StatusBadge status="pending" />
                      )}
                      {state && (
                        isExpanded
                          ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </div>
                  </button>

                  {/* Expanded content */}
                  {isExpanded && state && (
                    <div className="px-3 pb-3 space-y-2 border-t border-dashed mx-2">
                      {state.error && (
                        <div className="mt-2 rounded-md bg-red-50 border border-red-200 p-2">
                          <p className="text-xs font-medium text-red-700">Error</p>
                          <p className="text-xs text-red-600 mt-0.5 whitespace-pre-wrap">{state.error}</p>
                        </div>
                      )}

                      {state.resolvedConfig && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-muted-foreground mb-1">Resolved Config (after interpolation)</p>
                          <pre className="text-[11px] bg-white rounded border p-2 overflow-auto max-h-32 font-mono text-muted-foreground whitespace-pre-wrap">
                            {JSON.stringify(state.resolvedConfig, null, 2)}
                          </pre>
                        </div>
                      )}

                      {state.output && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Output</p>
                          <pre className="text-[11px] bg-white rounded border p-2 overflow-auto max-h-48 font-mono whitespace-pre-wrap">
                            {JSON.stringify(state.output, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Context summary */}
          {Object.keys(nodeOutputs).length > 0 && (
            <>
              <Separator />
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Variables Context
                </label>
                <p className="text-[10px] text-muted-foreground">
                  All variables available to the next node via {"{{node_id.field}}"} syntax
                </p>
                <pre className="text-[11px] bg-muted/50 rounded-lg border p-3 overflow-auto max-h-64 font-mono whitespace-pre-wrap">
                  {JSON.stringify(nodeOutputs, null, 2)}
                </pre>
              </div>

              {/* Evaluate Expression */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Terminal className="h-3 w-3" /> Evaluate Expression
                </label>
                <div className="flex gap-2">
                  <AutocompleteInput
                    className="font-mono text-xs flex-1"
                    placeholder="e.g. run_query_1.rows[0].customer_id"
                    value={evalExpr}
                    onChange={setEvalExpr}
                    suggestions={buildPlainSuggestions(nodeOutputs)}
                    plainMode
                    onSubmit={() => { if (evalExpr.trim()) handleEvaluate(); }}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleEvaluate}
                    disabled={!evalExpr.trim()}
                  >
                    Eval
                  </Button>
                </div>

                {evalResult && (
                  <div className={`rounded-md border p-2 ${evalResult.error ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                    {evalResult.error ? (
                      <p className="text-xs text-red-600">{evalResult.error}</p>
                    ) : (
                      <pre className="text-[11px] font-mono whitespace-pre-wrap overflow-auto max-h-32">
                        {typeof evalResult.value === "object"
                          ? JSON.stringify(evalResult.value, null, 2)
                          : String(evalResult.value ?? "undefined")}
                      </pre>
                    )}
                  </div>
                )}

                {evalHistory.length > 1 && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-muted-foreground font-medium">History</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {evalHistory.slice(1).map((h, i) => (
                        <button
                          key={i}
                          className="w-full text-left flex items-center gap-2 text-[11px] font-mono hover:bg-muted/50 rounded px-1.5 py-0.5"
                          onClick={() => { setEvalExpr(h.expr); setEvalResult({ value: h.value, error: h.error }); }}
                        >
                          <span className="text-muted-foreground truncate flex-1">{h.expr}</span>
                          <span className={`shrink-0 truncate max-w-[200px] ${h.error ? "text-red-500" : "text-green-700"}`}>
                            {h.error ? h.error : typeof h.value === "object" ? JSON.stringify(h.value) : String(h.value ?? "undefined")}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer controls */}
        <div className="px-6 py-4 border-t shrink-0 flex items-center justify-between gap-3">
          <Button variant="outline" size="sm" onClick={reset} disabled={stepping || currentIdx === 0}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reset
          </Button>
          <div className="flex items-center gap-2">
            {allDone && !hasFailed && (
              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> All steps completed
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={stepOne}
              disabled={stepping || allDone || hasFailed}
            >
              {stepping ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
              Step
            </Button>
            <Button
              size="sm"
              onClick={runAll}
              disabled={stepping || allDone || hasFailed}
            >
              {stepping ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
              Run All
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// TemplateBrowserDialog and TemplatePromptDialog imported from @/components/TemplateBrowserDialog

// ────────────────────────────────────────────────────────────
// NL Workflow Builder Dialog
// ────────────────────────────────────────────────────────────

interface ChatSampleData {
  columns: string[];
  rows: Record<string, unknown>[];
}

interface ChatMessage {
  /** DB message ID – populated after persistence */
  id?: string;
  role: "user" | "assistant";
  content: string;
  /** If the assistant generated a workflow, it's stored here */
  workflow?: WorkflowDefinition;
  workflow_name?: string;
  workflow_description?: string;
  /** assistant response type */
  type?: "question" | "workflow" | "message";
  /** proposed SQL query to show as a preview */
  proposed_sql?: string;
  /** sample rows from the proposed SQL */
  sample_data?: ChatSampleData;
}

/** A workflow builder session entry shown in the history sidebar */
interface WorkflowSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  workflow_id: string | null;
  workflow_name: string | null;
}

const WELCOME_MSG: ChatMessage = {
  role: "assistant",
  content:
    "Hi! Tell me what you'd like to automate and I'll build the workflow for you.\n\nFor example: *\"Schedule a mail to me@example.com every hour with last hour's search count per airport\"*",
  type: "message",
};

function NLWorkflowBuilderDialog({
  open,
  onClose,
  onOpenInEditor,
  initialConversationId,
  initialPrompt,
}: {
  open: boolean;
  onClose: () => void;
  /** Called when user wants to open the generated workflow in the normal editor */
  onOpenInEditor: (nodes: WorkflowNode[], name: string, description: string) => void;
  /** Pre-load a specific past session when the dialog opens */
  initialConversationId?: string | null;
  /** Auto-send this prompt when the dialog opens (from template selection) */
  initialPrompt?: string | null;
}) {
  const { profile, user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MSG]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [savedWorkflowId, setSavedWorkflowId] = useState<string | null>(null);
  // Persistence state
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionList, setSessionList] = useState<WorkflowSession[]>([]);
  const [loadingSession, setLoadingSession] = useState(false);
  const [debugMessageId, setDebugMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Scroll chat to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load list of past workflow builder sessions for this user
  const loadSessionList = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("conversations")
      .select("id, title, created_at, updated_at, workflow_id, workflows(name)")
      .eq("user_id", user.id)
      .eq("type", "workflow")
      .is("deleted_at", null)
      .order("updated_at", { ascending: false })
      .limit(50);
    const sessions: WorkflowSession[] = (data ?? []).map((row: any) => ({
      id: row.id,
      title: row.title,
      created_at: row.created_at,
      updated_at: row.updated_at,
      workflow_id: row.workflow_id ?? null,
      workflow_name: row.workflows?.name ?? null,
    }));
    setSessionList(sessions);
  }, [user]);

  // Restore a past session from the DB
  const loadSession = async (convId: string) => {
    setLoadingSession(true);
    try {
      const { data: msgs } = await supabase
        .from("messages")
        .select("id, role, content, metadata, created_at")
        .eq("conversation_id", convId)
        .order("created_at", { ascending: true });
      const chatMessages: ChatMessage[] = (msgs ?? []).map((m: any) => ({
        id: m.id as string | undefined,
        role: m.role as "user" | "assistant",
        content: m.content ?? "",
        type: (m.metadata as any)?.type,
        proposed_sql: (m.metadata as any)?.proposed_sql,
        sample_data: (m.metadata as any)?.sample_data,
        workflow: (m.metadata as any)?.workflow,
        workflow_name: (m.metadata as any)?.workflow_name,
        workflow_description: (m.metadata as any)?.workflow_description,
      }));
      setMessages(chatMessages.length > 0 ? chatMessages : [WELCOME_MSG]);
      setConversationId(convId);
      setSavedWorkflowId(null);
      setInputValue("");
    } finally {
      setLoadingSession(false);
    }
  };

  // Start a fresh session
  const startNewSession = () => {
    setMessages([WELCOME_MSG]);
    setConversationId(null);
    setSavedWorkflowId(null);
    setInputValue("");
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const deleteSession = async (sessId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Soft-delete: mark as deleted instead of removing
    const { error } = await supabase.from("conversations").update({ deleted_at: new Date().toISOString(), deleted_by: user?.id } as any).eq("id", sessId);
    if (error) {
      toast.error("Failed to delete session");
      return;
    }
    setSessionList((prev) => prev.filter((s) => s.id !== sessId));
    if (conversationId === sessId) startNewSession();
  };

  const initialPromptSentRef = useRef(false);

  // On dialog open: load session list; auto-load initialConversationId if provided
  useEffect(() => {
    if (open) {
      loadSessionList();
      if (initialConversationId && conversationId !== initialConversationId) {
        loadSession(initialConversationId);
      } else if (!conversationId) {
        setMessages([WELCOME_MSG]);
        setInputValue("");
        setSavedWorkflowId(null);
      }
      setTimeout(() => inputRef.current?.focus(), 100);

      // Auto-send template prompt if provided
      if (initialPrompt && !initialPromptSentRef.current) {
        initialPromptSentRef.current = true;
        // Small delay so the dialog is rendered before sending
        setTimeout(() => sendMessage(initialPrompt), 200);
      }
    } else {
      initialPromptSentRef.current = false;
    }
  }, [open, initialConversationId]);

  // Build conversation history for the API — include all structured context
  // so Claude sees the full picture (proposed SQL, sample data, workflow JSON)
  const buildHistory = (msgs: ChatMessage[]) =>
    msgs
      .filter((m) => m !== WELCOME_MSG) // exclude static welcome message
      .map((m) => {
        let content = m.content;
        if (m.type === "workflow" && m.workflow) {
          content += `\n\nWorkflow JSON: ${JSON.stringify({ nodes: m.workflow.nodes }, null, 2)}`;
        } else if (m.type === "question") {
          if (m.proposed_sql) content += `\n\nProposed SQL:\n\`\`\`sql\n${m.proposed_sql}\n\`\`\``;
          if (m.sample_data?.rows?.length) {
            content += `\n\nSample data (${m.sample_data.rows.length} rows): ${JSON.stringify(m.sample_data.rows)}`;
          }
        }
        return { role: m.role, content };
      });

  const handleStopChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setLoading(false);
  };

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    const userMsg: ChatMessage = { role: "user", content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInputValue("");
    setLoading(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    let convId = conversationId;

    try {
      // Create a DB conversation on the very first user message
      if (!convId) {
        const { data: conv, error: convErr } = await supabase
          .from("conversations")
          .insert({
            title: text.slice(0, 60) + (text.length > 60 ? "…" : ""),
            type: "workflow",
            user_id: user!.id,
            client_id: profile?.client_id ?? "",
            selected_data_source_ids: [],
          } as any)
          .select("id")
          .single();
        if (convErr) throw convErr;
        convId = (conv as any).id as string;
        setConversationId(convId);
        loadSessionList(); // refresh sidebar
      }

      // Persist the user message
      await supabase.from("messages").insert({
        conversation_id: convId,
        role: "user",
        content: text,
        client_id: profile?.client_id ?? "",
        status: "complete",
      } as any);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();

      // History excludes the new user message we just added (it's the current message)
      const history = buildHistory(messages);

      const res = await fetch(`${supabaseUrl}/functions/v1/nl-workflow-builder`, {
        method: "POST",
        headers,
        signal: controller.signal,
        body: JSON.stringify({
          message: text,
          history,
          user_context: {
            email: user?.email ?? null,
            name: profile?.full_name ?? null,
          },
          conversationId: convId,
          clientId: profile?.client_id ?? "",
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed: ${res.status}`);
      }

      const data = await res.json() as {
        type: "question" | "workflow" | "message";
        content: string;
        proposed_sql?: string;
        sample_data?: ChatSampleData;
        workflow?: WorkflowDefinition;
        workflow_name?: string;
        workflow_description?: string;
      };

      const assistantMsg: ChatMessage = {
        role: "assistant",
        content: data.content,
        type: data.type,
        proposed_sql: data.proposed_sql,
        sample_data: data.sample_data,
        workflow: data.workflow,
        workflow_name: data.workflow_name,
        workflow_description: data.workflow_description,
      };

      // Persist assistant message with workflow-specific metadata; capture the DB id
      const { data: savedMsg } = await supabase.from("messages").insert({
        conversation_id: convId,
        role: "assistant",
        content: assistantMsg.content,
        client_id: profile?.client_id ?? "",
        status: "complete",
        metadata: {
          type: assistantMsg.type ?? null,
          proposed_sql: assistantMsg.proposed_sql ?? null,
          sample_data: assistantMsg.sample_data ?? null,
          workflow: assistantMsg.workflow ?? null,
          workflow_name: assistantMsg.workflow_name ?? null,
          workflow_description: assistantMsg.workflow_description ?? null,
        },
      } as any).select("id").single();

      const assistantMsgWithId: ChatMessage = {
        ...assistantMsg,
        id: (savedMsg as any)?.id ?? undefined,
      };
      setMessages([...updatedMessages, assistantMsgWithId]);
      setSavedWorkflowId(null); // reset saved state when new workflow arrives

    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        // User stopped the chat — add a notice message
        const stoppedMsg: ChatMessage = {
          role: "assistant",
          content: "Chat stopped by user.",
          type: "message",
        };
        setMessages([...updatedMessages, stoppedMsg]);
      } else {
        const errorMsg: ChatMessage = {
          role: "assistant",
          content: `Sorry, something went wrong: ${err instanceof Error ? err.message : "Unknown error"}. Please try again.`,
          type: "message",
        };
        setMessages([...updatedMessages, errorMsg]);
      }
    } finally {
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  // Save the generated workflow to the database
  const handleSave = async (wfMsg: ChatMessage) => {
    if (!wfMsg.workflow || !profile?.client_id) return;
    setSaving(true);
    try {
      const workflowName = wfMsg.workflow_name || "AI Generated Workflow";

      const { data: inserted, error } = await supabase
        .from("workflows")
        .insert({
          name: workflowName,
          description: wfMsg.workflow_description || null,
          definition: wfMsg.workflow as unknown as Json,
          client_id: profile.client_id,
        })
        .select("id")
        .single();
      if (error) throw error;
      setSavedWorkflowId(inserted.id);

      // Raw SQL is stored directly in each query node's config.query field.
      // Set _queryName from the label for display purposes.
      const queryNodes = wfMsg.workflow.nodes.filter((n) => n.type === "run_query" || n.type === "loop");
      if (queryNodes.length > 0) {
        for (const n of queryNodes) {
          const sql = String(n.config?.query ?? "").trim();
          if (!sql) continue;
          const queryName = String(n.config?.label ?? "").trim() || `${workflowName} – ${n.id}`;
          n.config = { ...n.config, _queryName: queryName };
        }
        // Update workflow definition with _queryName labels
        await supabase
          .from("workflows")
          .update({ definition: { nodes: wfMsg.workflow.nodes } as unknown as Json })
          .eq("id", inserted.id);
      }

      // Link the conversation to the saved workflow
      if (conversationId && inserted?.id) {
        await supabase
          .from("conversations")
          .update({ workflow_id: inserted.id } as any)
          .eq("id", conversationId);
        loadSessionList(); // refresh sidebar to show the link
      }
      toast.success("Workflow saved successfully!");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to save workflow");
    } finally {
      setSaving(false);
    }
  };

  // Run the saved workflow
  const handleRunSaved = async () => {
    if (!savedWorkflowId) return;
    setRunning(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();
      const res = await fetch(`${supabaseUrl}/functions/v1/workflow-runner/run`, {
        method: "POST",
        headers,
        body: JSON.stringify({ workflow_id: savedWorkflowId }),
      });
      const result = await res.json();
      if (result.status === "success") {
        toast.success("Workflow executed successfully!");
      } else {
        toast.error(`Execution failed: ${result.error ?? "Unknown error"}`);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Run failed");
    } finally {
      setRunning(false);
    }
  };

  // Find the last workflow message
  const lastWorkflowMsg = [...messages].reverse().find((m) => m.type === "workflow");

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl sm:max-w-4xl h-[90vh] flex flex-row p-0 gap-0 overflow-hidden">

        {/* ── Session sidebar ──────────────────────────────────────────────── */}
        <div className="w-52 shrink-0 border-r flex flex-col bg-muted/30 rounded-l-lg overflow-hidden">
          <div className="px-3 py-3 border-b flex items-center justify-between shrink-0">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sessions</span>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={startNewSession}
              title="New session"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {loadingSession ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : sessionList.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center px-3 py-6">No previous sessions.</p>
            ) : (
              sessionList.map((sess) => (
                <div
                  key={sess.id}
                  className={`group relative w-full text-left px-3 py-2.5 text-xs hover:bg-muted/60 transition-colors cursor-pointer ${
                    conversationId === sess.id ? "bg-muted/80" : ""
                  }`}
                  onClick={() => loadSession(sess.id)}
                >
                  {/* Title + delete button row */}
                  <div className="flex items-start gap-1 pr-5">
                    <p className="flex-1 truncate text-foreground font-medium leading-snug">{sess.title}</p>
                  </div>

                  {/* Last updated */}
                  <p className="text-muted-foreground/60 text-[10px] mt-0.5 flex items-center gap-1">
                    <Clock className="h-2.5 w-2.5 shrink-0" />
                    {new Date(sess.updated_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>

                  {/* Linked workflow badge */}
                  {sess.workflow_name && (
                    <p className="mt-1 flex items-center gap-1 text-[10px] text-violet-700 font-medium truncate">
                      <Zap className="h-2.5 w-2.5 shrink-0" />
                      {sess.workflow_name}
                    </p>
                  )}

                  {/* Delete button — appears on hover */}
                  <button
                    onClick={(e) => deleteSession(sess.id, e)}
                    className="absolute right-2 top-2 hidden group-hover:flex items-center justify-center h-5 w-5 rounded hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors"
                    title="Delete session"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Main chat area ───────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
            <DialogTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-600" />
              Build Workflow with AI
              {conversationId && (() => {
                const active = sessionList.find((s) => s.id === conversationId);
                return active?.workflow_name ? (
                  <span className="ml-auto flex items-center gap-1 text-xs font-normal text-violet-700">
                    <Zap className="h-3 w-3" />
                    {active.workflow_name}
                  </span>
                ) : (
                  <span className="ml-auto text-xs font-normal text-muted-foreground">Session active</span>
                );
              })()}
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Describe your automation in plain English — the AI will ask any clarifying questions and build the workflow for you.
            </p>
          </DialogHeader>

          {/* Chat messages */}
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
              >
                {/* Avatar */}
                <div
                  className={`shrink-0 rounded-full w-7 h-7 flex items-center justify-center text-white text-xs ${
                    msg.role === "user" ? "bg-primary" : "bg-violet-600"
                  }`}
                >
                  {msg.role === "user" ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                </div>

                <div className={`flex-1 min-w-0 space-y-2 ${msg.role === "user" ? "items-end" : "items-start"} flex flex-col`}>
                  {/* Message bubble */}
                  <div
                    className={`rounded-xl px-4 py-3 text-sm max-w-[85%] whitespace-pre-wrap ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground ml-auto"
                        : msg.type === "workflow"
                        ? "bg-violet-50 border border-violet-200 text-foreground"
                        : msg.type === "question"
                        ? "bg-amber-50 border border-amber-200 text-foreground"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    {msg.type === "question" && (
                      <div className="flex items-center gap-1.5 mb-2 text-amber-700 font-medium text-xs">
                        <span>Question</span>
                      </div>
                    )}
                    {msg.type === "workflow" && (
                      <div className="flex items-center gap-1.5 mb-2 text-violet-700 font-medium text-xs">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>Workflow generated</span>
                      </div>
                    )}
                    {msg.content}
                  </div>

                  {/* Debug icon for assistant messages */}
                  {msg.role === "assistant" && msg.id && (
                    <button
                      onClick={() => setDebugMessageId(msg.id!)}
                      className="flex items-center gap-1 text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors mt-0.5"
                      title="Debug – see what was sent to LLM"
                    >
                      <Bug className="h-3 w-3" />
                      Debug
                    </button>
                  )}

                  {/* SQL preview + sample data (shown on question messages) */}
                  {msg.role === "assistant" && (msg.proposed_sql || msg.sample_data) && (
                    <div className="w-full max-w-[85%] space-y-2">
                      {msg.proposed_sql && (
                        <div className="rounded-xl border bg-card overflow-hidden">
                          <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/40">
                            <Database className="h-3.5 w-3.5 text-indigo-600" />
                            <span className="text-xs font-semibold text-indigo-700">Proposed SQL Query</span>
                          </div>
                          <pre className="text-xs font-mono text-foreground p-3 overflow-x-auto whitespace-pre-wrap bg-slate-50">
                            {msg.proposed_sql}
                          </pre>
                        </div>
                      )}
                      {msg.sample_data && msg.sample_data.columns.length > 0 && (
                        <div className="rounded-xl border bg-card overflow-hidden">
                          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/40">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                              <span className="text-xs font-semibold text-green-700">Sample Results</span>
                            </div>
                            <span className="text-xs text-muted-foreground">{msg.sample_data.rows.length} row{msg.sample_data.rows.length !== 1 ? "s" : ""}</span>
                          </div>
                          <div className="overflow-x-auto max-h-[220px] overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b bg-muted/20 sticky top-0">
                                  {msg.sample_data.columns.map((col) => (
                                    <th key={col} className="px-3 py-1.5 text-left font-semibold text-foreground whitespace-nowrap">
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {msg.sample_data.rows.map((row, ri) => (
                                  <tr key={ri} className="border-b last:border-0 hover:bg-muted/20">
                                    {msg.sample_data!.columns.map((col) => (
                                      <td key={col} className="px-3 py-1.5 text-muted-foreground whitespace-nowrap max-w-[180px] truncate">
                                        {row[col] === null ? (
                                          <span className="italic text-muted-foreground/40">null</span>
                                        ) : typeof row[col] === "object" ? (
                                          JSON.stringify(row[col])
                                        ) : (
                                          String(row[col])
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {msg.sample_data && msg.sample_data.columns.length === 0 && (
                        <div className="rounded-xl border bg-amber-50 border-amber-200 px-3 py-2 flex items-center gap-2 text-xs text-amber-700">
                          <XCircle className="h-3.5 w-3.5 shrink-0" />
                          Query returned 0 rows — consider adjusting the filter or time range.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Workflow preview + actions */}
                  {msg.type === "workflow" && msg.workflow && (
                    <div className="w-full max-w-[85%] space-y-2">
                      {/* Workflow preview */}
                      <div className="rounded-xl border bg-card p-4 space-y-3">
                        <div>
                          <h4 className="text-sm font-semibold">{msg.workflow_name}</h4>
                          {msg.workflow_description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{msg.workflow_description}</p>
                          )}
                        </div>

                        {/* Node flow */}
                        <div className="space-y-1.5">
                          {msg.workflow.nodes.map((node, ni) => {
                            const meta = NODE_META[node.type as NodeType];
                            if (!meta) return null;
                            const Icon = meta.icon;
                            return (
                              <div key={node.id} className="flex items-start gap-2">
                                {ni > 0 && (
                                  <div className="ml-3 flex items-center justify-center w-4">
                                    <ArrowDown className="h-3.5 w-3.5 text-muted-foreground/50" />
                                  </div>
                                )}
                                {ni === 0 && <div className="w-4" />}
                                <span
                                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${meta.bg} ${meta.color}`}
                                >
                                  <Icon className="h-3 w-3" />
                                  {meta.label}
                                </span>
                                <code className="text-xs text-muted-foreground self-center">{node.id}</code>
                              </div>
                            );
                          })}
                        </div>

                        {/* JSON details (collapsible) */}
                        <WorkflowJsonPreview nodes={msg.workflow.nodes} />
                      </div>

                      {/* Action buttons */}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          className="h-8 gap-1.5 text-xs bg-violet-600 hover:bg-violet-700"
                          onClick={() => handleSave(msg)}
                          disabled={saving || !!savedWorkflowId}
                        >
                          {saving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : savedWorkflowId ? (
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          ) : (
                            <Plus className="h-3.5 w-3.5" />
                          )}
                          {savedWorkflowId ? "Saved" : "Save Workflow"}
                        </Button>

                        {savedWorkflowId && (
                          <Button
                            size="sm"
                            className="h-8 gap-1.5 text-xs"
                            onClick={handleRunSaved}
                            disabled={running}
                          >
                            {running ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Play className="h-3.5 w-3.5" />
                            )}
                            Run Now
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1.5 text-xs"
                          onClick={() => {
                            if (msg.workflow) {
                              onOpenInEditor(
                                msg.workflow.nodes,
                                msg.workflow_name || "AI Workflow",
                                msg.workflow_description || "",
                              );
                            }
                          }}
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                          Open in Editor
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="flex gap-3 items-start">
                <div className="shrink-0 rounded-full w-7 h-7 flex items-center justify-center bg-violet-600 text-white">
                  <Bot className="h-3.5 w-3.5" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="rounded-xl px-4 py-3 bg-muted text-sm flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Thinking…
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1.5 text-xs border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={handleStopChat}
                    title="Stop generating"
                  >
                    <Square className="h-3 w-3 fill-current" />
                    Stop
                  </Button>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Refine hint when workflow is ready */}
          {lastWorkflowMsg && (
            <div className="px-4 py-2 border-t bg-muted/30 shrink-0">
              <p className="text-xs text-muted-foreground text-center">
                Not quite right? Describe what to change and the AI will regenerate.
              </p>
            </div>
          )}

          {/* Input area */}
          <div className="px-4 py-4 border-t shrink-0">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                className="flex-1 min-h-[60px] max-h-[120px] resize-none rounded-xl border bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/40 disabled:opacity-50"
                placeholder="Describe your automation… (Enter to send, Shift+Enter for new line)"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />
              {loading ? (
                <Button
                  size="icon"
                  className="h-10 w-10 shrink-0 bg-red-600 hover:bg-red-700 rounded-xl"
                  onClick={handleStopChat}
                  title="Stop generating"
                >
                  <Square className="h-4 w-4 fill-current" />
                </Button>
              ) : (
                <Button
                  size="icon"
                  className="h-10 w-10 shrink-0 bg-violet-600 hover:bg-violet-700 rounded-xl"
                  onClick={() => sendMessage(inputValue)}
                  disabled={!inputValue.trim()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Per-message LLM debug dialog */}
    <ConversationDebugDialog
      messageId={debugMessageId}
      conversationId={conversationId}
      open={!!debugMessageId}
      onOpenChange={(open) => !open && setDebugMessageId(null)}
    />
  </>
  );
}

// ────────────────────────────────────────────────────────────
// Collapsible JSON preview used inside the AI chat
// ────────────────────────────────────────────────────────────
function WorkflowJsonPreview({ nodes }: { nodes: WorkflowNode[] }) {
  const [expanded, setExpanded] = useState(false);
  const json = JSON.stringify({ nodes }, null, 2);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground font-medium"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {expanded ? "Hide" : "View"} JSON definition
      </button>
      {expanded && (
        <pre className="mt-2 text-xs font-mono bg-muted/60 border rounded-lg p-3 max-h-[280px] overflow-auto whitespace-pre text-muted-foreground">
          {json}
        </pre>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Workflow Card
// ────────────────────────────────────────────────────────────
function WorkflowCard({
  workflow,
  onEdit,
  onDelete,
  onHistory,
  onRun,
  onClone,
  running,
  linkedConversation,
  onContinueInBuilder,
}: {
  workflow: Workflow;
  onEdit: () => void;
  onDelete: () => void;
  onHistory: () => void;
  onRun: () => void;
  onClone: () => void;
  running: boolean;
  linkedConversation?: { id: string; title: string } | null;
  onContinueInBuilder?: () => void;
}) {
  const nodeTypes = [...new Set(workflow.definition.nodes.map((n) => n.type))];

  return (
    <div className="rounded-xl border bg-card p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-sm">{toCamelCase(workflow.name)}</h3>
            {workflow.is_active ? (
              <Badge variant="outline" className="text-xs text-green-600 border-green-300 shrink-0">
                Active
              </Badge>
            ) : (
              <Badge variant="secondary" className="text-xs shrink-0">
                Inactive
              </Badge>
            )}
          </div>
          {workflow.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{workflow.description}</p>
          )}
        </div>
      </div>

      {/* Node type pills showing the workflow shape */}
      <div className="flex flex-wrap gap-1.5 items-center">
        {nodeTypes.map((type, i) => {
          const meta = NODE_META[type as NodeType];
          if (!meta) return null;
          const Icon = meta.icon;
          return (
            <span key={type} className="inline-flex items-center gap-1">
              {i > 0 && <span className="text-muted-foreground/40 text-xs">→</span>}
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${meta.bg} ${meta.color}`}
              >
                <Icon className="h-3 w-3" />
                {meta.label}
              </span>
            </span>
          );
        })}
        <span className="text-xs text-muted-foreground ml-auto">
          {workflow.definition.nodes.length} {workflow.definition.nodes.length === 1 ? "node" : "nodes"}
        </span>
      </div>

      {/* Timestamps */}
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground border-t pt-2">
        <span className="flex items-center gap-1" title={workflow.updated_at}>
          <Edit3 className="h-3 w-3 shrink-0" />
          Updated {formatRelativeTime(workflow.updated_at)}
        </span>
        <span className="flex items-center gap-1" title={workflow.last_run_at ?? "Never run"}>
          <Play className="h-3 w-3 shrink-0" />
          Last run {formatRelativeTime(workflow.last_run_at)}
        </span>
      </div>

      {/* Linked AI builder conversation */}
      {linkedConversation && (
        <button
          onClick={onContinueInBuilder}
          className="flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs text-violet-700 hover:bg-violet-100 transition-colors text-left w-full"
        >
          <Sparkles className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate font-medium">Continue in AI Builder</span>
          <span className="truncate text-violet-500 ml-1 hidden sm:inline">— {linkedConversation.title}</span>
        </button>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button size="sm" className="h-8 gap-1.5 text-xs flex-1" onClick={onRun} disabled={running}>
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          Run
        </Button>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={onHistory}>
          <Clock className="h-3.5 w-3.5" />
          History
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} title="Edit">
          <Edit3 className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClone} title="Clone workflow">
          <Copy className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Main Workflows Page
// ────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────
// Task Detail Dialog – split-pane popup (messages | steps)
// ────────────────────────────────────────────────────────────
interface TaskMessage {
  id: string;
  message: string;
  type: string;
  subject: string | null;
  to_email: string | null;
  cc: string[] | null;
  bcc: string[] | null;
  attachments: { filename: string; url: string }[] | null;
  created_at: string;
  sender: { id: string; name: string; role: string } | null;
  receiver: { id: string; name: string; role: string } | null;
}

interface TaskActor {
  id: string;
  name: string;
  role: string;
}

interface AllExecution extends WorkflowExecution {
  workflow_name: string;
}

const MESSAGE_TYPE_META: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  email: { icon: Mail, color: "text-blue-600", label: "Email" },
  sms: { icon: Send, color: "text-green-600", label: "SMS" },
  whatsapp: { icon: Send, color: "text-emerald-600", label: "WhatsApp" },
  slack: { icon: Send, color: "text-purple-600", label: "Slack" },
};

function TaskDetailDialog({
  open,
  onClose,
  execution,
}: {
  open: boolean;
  onClose: () => void;
  execution: AllExecution | null;
}) {
  const [steps, setSteps] = useState<NodeExecution[]>([]);
  const [messages, setMessages] = useState<TaskMessage[]>([]);
  const [actors, setActors] = useState<TaskActor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !execution) return;
    setLoading(true);

    const loadData = async () => {
      const [stepsRes, msgsRes] = await Promise.all([
        supabase
          .from("task_steps")
          .select("*")
          .eq("task_id", execution.id)
          .order("created_at", { ascending: true }),
        supabase
          .from("task_message")
          .select("id, message, type, subject, to_email, cc, bcc, attachments, created_at, sender:actor!task_message_sender_id_fkey(id,name,role), receiver:actor!task_message_receiver_id_fkey(id,name,role)")
          .eq("task_id", execution.id)
          .order("created_at", { ascending: true }),
      ]);

      setSteps((stepsRes.data ?? []) as NodeExecution[]);
      const msgs = (msgsRes.data ?? []) as unknown as TaskMessage[];
      setMessages(msgs);

      // Derive unique actors from messages
      const actorMap = new Map<string, TaskActor>();
      for (const m of msgs) {
        if (m.sender) actorMap.set(m.sender.id, m.sender);
        if (m.receiver) actorMap.set(m.receiver.id, m.receiver);
      }
      setActors(Array.from(actorMap.values()));
      setLoading(false);
    };

    loadData();
  }, [open, execution]);

  const dur = (s: string | null, e: string | null) => {
    if (!s || !e) return "";
    const ms = new Date(e).getTime() - new Date(s).getTime();
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  };

  const roleColor: Record<string, string> = {
    ai_employee: "bg-violet-100 text-violet-700 border-violet-200",
    human_employee: "bg-blue-100 text-blue-700 border-blue-200",
    customer: "bg-amber-100 text-amber-700 border-amber-200",
  };

  if (!execution) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              {execution.tid && (
                <span className="text-sm font-mono font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded">
                  {execution.tid}
                </span>
              )}
              <h2 className="text-base font-semibold">{execution.workflow_name}</h2>
              <span className="text-xs text-muted-foreground">
                {new Date(execution.created_at).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {execution.started_at && execution.completed_at && (
                <span className="text-xs text-muted-foreground">
                  {dur(execution.started_at, execution.completed_at)}
                </span>
              )}
              <StatusBadge status={execution.status} />
            </div>
          </div>
          {execution.error && (
            <p className="text-xs text-destructive mb-2 max-w-2xl truncate">{execution.error}</p>
          )}
          {actors.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Users className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground mr-1">Actors:</span>
              {actors.map((a) => (
                <Badge key={a.id} variant="outline" className={`text-xs ${roleColor[a.role] ?? ""}`}>
                  {a.role === "ai_employee" ? <Bot className="h-3 w-3 mr-1" /> : <User className="h-3 w-3 mr-1" />}
                  {a.name}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Body: split pane */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 flex min-h-0">
            {/* Left: Messages */}
            <div className="w-1/2 border-r flex flex-col min-h-0">
              <div className="px-4 py-3 border-b bg-muted/20 shrink-0">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Messages</span>
                  <Badge variant="secondary" className="text-xs ml-auto">{messages.length}</Badge>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                    <Mail className="h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">No messages for this task.</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {messages.map((msg) => {
                      const meta = MESSAGE_TYPE_META[msg.type];
                      const MsgIcon = meta?.icon ?? Mail;
                      const isEmail = msg.type === "email";
                      const attachments = msg.attachments as { filename: string; url: string }[] | null;
                      return (
                        <div key={msg.id} className="px-4 py-3">
                          {/* Type badge + timestamp */}
                          <div className="flex items-center gap-2 mb-2">
                            <MsgIcon className={`h-3.5 w-3.5 ${meta?.color ?? "text-muted-foreground"}`} />
                            <Badge variant="outline" className="text-[10px]">{meta?.label ?? msg.type}</Badge>
                            <span className="text-[10px] text-muted-foreground ml-auto">
                              {new Date(msg.created_at).toLocaleString()}
                            </span>
                          </div>

                          {isEmail ? (
                            /* Email: full headers + body without scroll */
                            <div className="rounded-lg border bg-white">
                              {/* Subject */}
                              {msg.subject && (
                                <div className="px-3 pt-3 pb-2 border-b bg-muted/10">
                                  <h4 className="text-sm font-semibold">{msg.subject}</h4>
                                </div>
                              )}
                              {/* Email headers table */}
                              <div className="px-3 py-2 border-b text-xs space-y-1">
                                <div className="flex gap-2">
                                  <span className="text-muted-foreground w-10 shrink-0 font-medium">From</span>
                                  <span>{msg.sender?.name ?? "Unknown"}{msg.sender ? ` <reports@updates.databull.ai>` : ""}</span>
                                </div>
                                <div className="flex gap-2">
                                  <span className="text-muted-foreground w-10 shrink-0 font-medium">To</span>
                                  <span>{msg.receiver?.name ? `${msg.receiver.name} ` : ""}{msg.to_email ? `<${msg.to_email}>` : "Unknown"}</span>
                                </div>
                                {msg.cc && msg.cc.length > 0 && (
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground w-10 shrink-0 font-medium">Cc</span>
                                    <span>{msg.cc.join(", ")}</span>
                                  </div>
                                )}
                                {msg.bcc && msg.bcc.length > 0 && (
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground w-10 shrink-0 font-medium">Bcc</span>
                                    <span>{msg.bcc.join(", ")}</span>
                                  </div>
                                )}
                              </div>
                              {/* Email body — full content, no scroll */}
                              {/<[a-z][\s\S]*>/i.test(msg.message) ? (
                                <div
                                  className="px-3 py-3 text-sm prose prose-sm max-w-none"
                                  dangerouslySetInnerHTML={{ __html: msg.message }}
                                />
                              ) : (
                                <div className="px-3 py-3 text-sm whitespace-pre-wrap">
                                  {msg.message}
                                </div>
                              )}
                              {/* Attachments */}
                              {attachments && attachments.length > 0 && (
                                <div className="px-3 py-2 border-t bg-muted/10">
                                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1.5">
                                    <Paperclip className="h-3 w-3" />
                                    <span className="font-medium">{attachments.length} Attachment{attachments.length > 1 ? "s" : ""}</span>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {attachments.map((att, i) => (
                                      <a
                                        key={i}
                                        href={att.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border bg-white hover:bg-muted/30 transition-colors"
                                      >
                                        <Paperclip className="h-3 w-3 text-muted-foreground" />
                                        {att.filename}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            /* Non-email: simple rendering */
                            <>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                                {msg.sender && <span>From: <span className="font-medium text-foreground">{msg.sender.name}</span></span>}
                                {msg.sender && msg.receiver && <span>&rarr;</span>}
                                {msg.receiver && <span>To: <span className="font-medium text-foreground">{msg.receiver.name}</span></span>}
                              </div>
                              <div className="text-xs whitespace-pre-wrap bg-muted/30 rounded p-2 border">
                                {msg.message}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Execution Steps */}
            <div className="w-1/2 flex flex-col min-h-0">
              <div className="px-4 py-3 border-b bg-muted/20 shrink-0">
                <div className="flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Execution Steps</span>
                  <Badge variant="secondary" className="text-xs ml-auto">{steps.length}</Badge>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {steps.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                    <ListChecks className="h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">No execution steps recorded.</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-2">
                    {steps.map((ne) => {
                      const meta = NODE_META[ne.node_type as NodeType];
                      const Icon = meta?.icon;
                      return (
                        <div key={ne.id} className={`rounded-lg border p-3 ${meta?.bg ?? "bg-muted/40"}`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {Icon && <Icon className={`h-3.5 w-3.5 ${meta?.color}`} />}
                              <span className="text-xs font-semibold">{ne.node_id}</span>
                              <span className="text-xs text-muted-foreground">({ne.node_type})</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {ne.started_at && ne.completed_at && (
                                <span className="text-xs text-muted-foreground">
                                  {dur(ne.started_at, ne.completed_at)}
                                </span>
                              )}
                              <StatusBadge status={ne.status} />
                            </div>
                          </div>
                          {ne.error && <p className="text-xs text-destructive mt-1">{ne.error}</p>}
                          {ne.output && (
                            <pre className="text-xs bg-white/60 rounded p-2 mt-2 overflow-auto max-h-36 text-muted-foreground border">
                              {JSON.stringify(ne.output, null, 2)}
                            </pre>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────
// Workflow Tasks panel – shows execution history across ALL workflows
// with a filter by workflow title
// ────────────────────────────────────────────────────────────

function WorkflowTasksPanel({ workflows, filterDepartmentId }: { workflows: Workflow[]; filterDepartmentId: string }) {
  const [executions, setExecutions] = useState<AllExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterWorkflowId, setFilterWorkflowId] = useState<string>("all");
  const [selectedExec, setSelectedExec] = useState<AllExecution | null>(null);

  // Scope workflows visible in this panel by department filter
  const scopedWorkflows = useMemo(() => {
    if (filterDepartmentId === "all") return workflows;
    return workflows.filter((w) => w.department_id === filterDepartmentId);
  }, [workflows, filterDepartmentId]);

  const workflowNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const w of workflows) m[w.id] = w.name;
    return m;
  }, [workflows]);

  // Reset workflow filter when department changes
  useEffect(() => {
    setFilterWorkflowId("all");
  }, [filterDepartmentId]);

  const loadExecutions = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("tasks")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);

    if (filterWorkflowId !== "all") {
      query = query.eq("workflow_id", filterWorkflowId);
    } else if (filterDepartmentId !== "all" && scopedWorkflows.length > 0) {
      query = query.in("workflow_id", scopedWorkflows.map((w) => w.id));
    }

    const { data } = await query;
    let rows: AllExecution[] = ((data ?? []) as WorkflowExecution[]).map((e) => ({
      ...e,
      workflow_name: workflowNameMap[e.workflow_id] ?? "Unknown Workflow",
    }));
    // If department filter active but no scoped workflows, show nothing
    if (filterDepartmentId !== "all" && scopedWorkflows.length === 0) {
      rows = [];
    }
    setExecutions(rows);
    setLoading(false);
  }, [filterWorkflowId, filterDepartmentId, scopedWorkflows, workflowNameMap]);

  useEffect(() => {
    loadExecutions();
  }, [loadExecutions]);

  const dur = (s: string | null, e: string | null) => {
    if (!s || !e) return "";
    const ms = new Date(e).getTime() - new Date(s).getTime();
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filter bar */}
      <div className="flex items-center gap-3 px-6 py-3 border-b bg-muted/20 shrink-0">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-muted-foreground">Filter by workflow:</span>
        <Select value={filterWorkflowId} onValueChange={setFilterWorkflowId}>
          <SelectTrigger className="w-[260px] h-8 text-sm">
            <SelectValue placeholder="All Workflows" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Workflows</SelectItem>
            {scopedWorkflows.map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 text-xs ml-auto"
          onClick={loadExecutions}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      {/* Execution list */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : executions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <History className="h-8 w-8 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold mb-2">No workflow tasks yet</h2>
            <p className="text-sm text-muted-foreground max-w-sm">
              Run a workflow to see execution history here.
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {executions.map((exec) => (
              <div key={exec.id} className="px-6 py-4 hover:bg-muted/20 transition-colors">
                <button className="w-full text-left" onClick={() => setSelectedExec(exec)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="flex items-center gap-2">
                          {exec.tid && (
                            <span className="text-xs font-mono font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                              {exec.tid}
                            </span>
                          )}
                          <p className="text-sm font-medium">{exec.workflow_name}</p>
                          <span className="text-xs text-muted-foreground">
                            {new Date(exec.created_at).toLocaleString()}
                          </span>
                        </div>
                        {exec.error && (
                          <p className="text-xs text-destructive mt-0.5 max-w-md truncate">{exec.error}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {exec.started_at && exec.completed_at && (
                        <span className="text-xs text-muted-foreground">
                          {dur(exec.started_at, exec.completed_at)}
                        </span>
                      )}
                      <StatusBadge status={exec.status} />
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <TaskDetailDialog
        open={!!selectedExec}
        onClose={() => setSelectedExec(null)}
        execution={selectedExec}
      />
    </div>
  );
}

export default function Workflows() {
  const { profile, user } = useAuth();
  const { log: auditLog } = useAuditLog();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState<Workflow | null>(null);
  const [historyWf, setHistoryWf] = useState<Workflow | null>(null);
  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());
  const [restoredDraft, setRestoredDraft] = useState<WorkflowDraft | null>(null);
  const [nlBuilderOpen, setNlBuilderOpen] = useState(false);
  const [nlBuilderConvId, setNlBuilderConvId] = useState<string | null>(null);
  // Map workflow_id → linked conversation {id, title}
  const [linkedConvMap, setLinkedConvMap] = useState<Record<string, { id: string; title: string }>>({});
  // Pre-populate state for "Open in Editor" from the NL builder
  const [nlDraftNodes, setNlDraftNodes] = useState<WorkflowNode[] | null>(null);
  const [nlDraftName, setNlDraftName] = useState("");
  const [nlDraftDescription, setNlDraftDescription] = useState("");
  // Template browser state
  const [templateBrowserOpen, setTemplateBrowserOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [templatePromptOpen, setTemplatePromptOpen] = useState(false);
  const [nlBuilderInitialPrompt, setNlBuilderInitialPrompt] = useState<string | null>(null);

  const [activePanel, setActivePanel] = useState<"setup" | "tasks">("setup");

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>("all");
  const [departments, setDepartments] = useState<Department[]>([]);

  const initialLoadDone = useRef(false);

  const fetchWorkflows = useCallback(async () => {
    // Only show full-page spinner on initial load, not on realtime refreshes
    if (!initialLoadDone.current) setLoading(true);
    const { data } = await supabase.from("workflows").select("*").is("deleted_at", null).order("created_at", { ascending: false });
    if (data) {
      setWorkflows(data as unknown as Workflow[]);
      // Fetch linked builder conversations for all workflows
      const ids = (data as { id: string }[]).map((w) => w.id);
      if (ids.length > 0) {
        const { data: convs } = await supabase
          .from("conversations")
          .select("id, title, workflow_id")
          .in("workflow_id", ids)
          .eq("type", "workflow")
          .order("updated_at", { ascending: false });
        // Keep only the most-recently-updated conversation per workflow
        const map: Record<string, { id: string; title: string }> = {};
        for (const c of ((convs ?? []) as unknown as { id: string; title: string; workflow_id: string }[])) {
          if (!map[c.workflow_id]) map[c.workflow_id] = { id: c.id, title: c.title };
        }
        setLinkedConvMap(map);
      }
    }
    setLoading(false);
    initialLoadDone.current = true;
  }, []);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  // Subscribe to realtime changes on the workflows table so the list
  // auto-refreshes when a new workflow is created (from any dialog or tab).
  useEffect(() => {
    const channel = supabase
      .channel("workflows-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "workflows" },
        () => {
          fetchWorkflows();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchWorkflows]);

  // Fetch departments for the filter dropdown
  useEffect(() => {
    if (!profile?.client_id) return;
    (async () => {
      const { data } = await supabase
        .from("department")
        .select("id, name")
        .eq("client_id", profile.client_id)
        .order("name");
      if (data) setDepartments(data as Department[]);
    })();
  }, [profile?.client_id]);

  // Filtered workflows by search query and department
  const filteredWorkflows = useMemo(() => {
    let result = workflows;
    if (filterDepartmentId !== "all") {
      result = result.filter((w) => w.department_id === filterDepartmentId);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((w) => w.name.toLowerCase().includes(q));
    }
    return result;
  }, [workflows, filterDepartmentId, searchQuery]);

  // Workflow count per department for sidebar badges
  const deptWorkflowCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const w of workflows) {
      const key = w.department_id ?? "_none";
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [workflows]);

  // Restore draft if user navigated back from /queries after selecting a query
  useEffect(() => {
    const raw = sessionStorage.getItem("wf_draft");
    if (raw) {
      try {
        const draft = JSON.parse(raw) as WorkflowDraft;
        sessionStorage.removeItem("wf_draft");
        setRestoredDraft(draft);
        setEditing(null);
        setBuilderOpen(true);
      } catch {
        sessionStorage.removeItem("wf_draft");
      }
    }
  }, []);

  const openCreate = () => {
    setEditing(null);
    setNlDraftNodes(null);
    setBuilderOpen(true);
  };
  const openEdit = (w: Workflow) => {
    setEditing(w);
    setBuilderOpen(true);
    auditLog({ action: "accessed", resourceType: "workflow", resourceId: w.id, resourceName: w.name });
  };

  // Called from NLWorkflowBuilderDialog to open the generated workflow in the standard editor
  const handleOpenInEditor = (nodes: WorkflowNode[], name: string, description: string) => {
    setNlBuilderOpen(false);
    setEditing(null);
    setNlDraftNodes(nodes);
    setNlDraftName(name);
    setNlDraftDescription(description);
    setBuilderOpen(true);
  };

  const handleDelete = async (w: Workflow) => {
    if (!confirm(`Delete "${w.name}"? It will be moved to the recycle bin.`)) return;
    const { error } = await supabase.from("workflows").update({ deleted_at: new Date().toISOString(), deleted_by: user?.id } as any).eq("id", w.id);
    if (error) {
      toast.error("Delete failed");
      return;
    }
    auditLog({ action: "deleted", resourceType: "workflow", resourceId: w.id, resourceName: w.name });
    toast.success("Workflow moved to recycle bin");
    fetchWorkflows();
  };

  const handleClone = async (w: Workflow) => {
    const { error } = await supabase.from("workflows").insert({
      name: `Copy of ${w.name}`,
      description: w.description,
      definition: w.definition as unknown as Json,
      client_id: profile?.client_id,
      is_active: false,
    });
    if (error) {
      toast.error("Clone failed");
      return;
    }
    auditLog({ action: "created", resourceType: "workflow", resourceName: `Copy of ${w.name}` });
    toast.success(`"${w.name}" cloned`);
    fetchWorkflows();
  };

  const handleRun = async (w: Workflow) => {
    if (runningIds.has(w.id)) return;
    setRunningIds((p) => new Set([...p, w.id]));
    try {
      const headers = await buildEdgeFunctionHeaders();

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/workflow-runner/run`, {
        method: "POST",
        headers,
        body: JSON.stringify({ workflow_id: w.id }),
      });
      const result = await res.json();
      if (result.status === "success") {
        toast.success(`"${w.name}" completed successfully`);
      } else {
        toast.error(`Workflow failed: ${result.error ?? "Unknown error"}`);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Run failed");
    } finally {
      setRunningIds((p) => {
        const n = new Set(p);
        n.delete(w.id);
        return n;
      });
    }
  };

  return (
    <div className="flex h-full w-full">
      {/* ── Sidebar ────────────────────────────────────────────────── */}
      <div className="w-56 shrink-0 border-r flex flex-col bg-muted/30">
        {/* Sidebar header */}
        <div className="px-4 py-4 border-b">
          <h1 className="text-base font-semibold">Agentic Workflows</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Automate multi-step processes
          </p>
        </div>

        {/* Navigation items */}
        <nav className="px-2 py-3 space-y-1">
          <button
            onClick={() => setActivePanel("setup")}
            className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              activePanel === "setup"
                ? "bg-background text-foreground shadow-sm border"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            }`}
          >
            <Settings2 className="h-4 w-4 shrink-0" />
            Setup Workflow
          </button>
          <button
            onClick={() => setActivePanel("tasks")}
            className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              activePanel === "tasks"
                ? "bg-background text-foreground shadow-sm border"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            }`}
          >
            <ListChecks className="h-4 w-4 shrink-0" />
            Workflow Tasks
          </button>
        </nav>

        {/* Department filter */}
        <div className="flex-1 overflow-auto border-t">
          <div className="px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Departments</span>
          </div>
          <div className="px-2 pb-2 space-y-0.5">
            <button
              onClick={() => setFilterDepartmentId("all")}
              className={`w-full flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
                filterDepartmentId === "all"
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              }`}
            >
              <Building2 className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 text-left">All Departments</span>
              <span className="text-xs tabular-nums text-muted-foreground">{workflows.length}</span>
            </button>
            {departments.map((dept) => (
              <button
                key={dept.id}
                onClick={() => setFilterDepartmentId(dept.id)}
                className={`w-full flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  filterDepartmentId === dept.id
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                }`}
              >
                <Building2 className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1 text-left">{dept.name}</span>
                {(deptWorkflowCounts[dept.id] ?? 0) > 0 && (
                  <span className="text-xs tabular-nums text-muted-foreground">{deptWorkflowCounts[dept.id]}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Sidebar footer — quick actions */}
        <div className="px-2 py-3 border-t space-y-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-xs h-8 text-amber-700 hover:text-amber-800 hover:bg-amber-50"
            onClick={() => setTemplateBrowserOpen(true)}
          >
            <LayoutTemplate className="h-3.5 w-3.5" />
            Templates
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-xs h-8 text-violet-700 hover:text-violet-800 hover:bg-violet-50"
            onClick={() => { setNlBuilderConvId(null); setNlBuilderInitialPrompt(null); setNlBuilderOpen(true); }}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Build with AI
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-xs h-8"
            onClick={openCreate}
          >
            <Plus className="h-3.5 w-3.5" />
            New Workflow
          </Button>
        </div>
      </div>

      {/* ── Main content area ──────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {activePanel === "setup" && (
          <>
            {/* Header bar */}
            <div className="flex items-center justify-between px-6 py-3 border-b bg-background shrink-0">
              <div>
                <h2 className="text-sm font-semibold">Setup Workflow</h2>
                <p className="text-xs text-muted-foreground">Create and manage your automated workflows</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs h-8 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                  onClick={() => setTemplateBrowserOpen(true)}
                >
                  <LayoutTemplate className="h-3.5 w-3.5" />
                  Templates
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs h-8 border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                  onClick={() => { setNlBuilderConvId(null); setNlBuilderInitialPrompt(null); setNlBuilderOpen(true); }}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Build with AI
                </Button>
                <Button
                  size="sm"
                  className="gap-1.5 text-xs h-8"
                  onClick={openCreate}
                >
                  <Plus className="h-3.5 w-3.5" />
                  New Workflow
                </Button>
              </div>
            </div>

            {/* Search & filter bar */}
            {!loading && workflows.length > 0 && (
              <div className="flex items-center gap-3 px-6 py-3 border-b bg-muted/20 shrink-0">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search workflows..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 pl-8 text-xs"
                  />
                </div>
                {departments.length > 0 && (
                  <Select value={filterDepartmentId} onValueChange={setFilterDepartmentId}>
                    <SelectTrigger className="h-8 w-[180px] text-xs">
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {(searchQuery || filterDepartmentId !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs gap-1"
                    onClick={() => { setSearchQuery(""); setFilterDepartmentId("all"); }}
                  >
                    <X className="h-3 w-3" />
                    Clear
                  </Button>
                )}
              </div>
            )}

            <div className="flex-1 overflow-auto px-6 py-6">
              {loading ? (
                <div className="flex items-center justify-center py-24">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : workflows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="rounded-full bg-muted p-4 mb-4">
                    <Zap className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h2 className="text-lg font-semibold mb-2">No workflows yet</h2>
                  <p className="text-sm text-muted-foreground max-w-sm mb-6">
                    Build your first workflow — run a query, check a condition, then send an email based on the result.
                  </p>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setTemplateBrowserOpen(true)}
                      className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                    >
                      <LayoutTemplate className="h-4 w-4" />
                      Templates
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setNlBuilderConvId(null); setNlBuilderInitialPrompt(null); setNlBuilderOpen(true); }}
                      className="gap-2 border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                    >
                      <Sparkles className="h-4 w-4" />
                      Build with AI
                    </Button>
                    <Button onClick={openCreate} className="gap-2">
                      <Plus className="h-4 w-4" />
                      Create manually
                    </Button>
                  </div>
                </div>
              ) : filteredWorkflows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="rounded-full bg-muted p-4 mb-4">
                    <Search className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h2 className="text-lg font-semibold mb-2">No matching workflows</h2>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Try adjusting your search or department filter.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredWorkflows.map((w) => (
                    <WorkflowCard
                      key={w.id}
                      workflow={w}
                      onEdit={() => openEdit(w)}
                      onDelete={() => handleDelete(w)}
                      onClone={() => handleClone(w)}
                      onHistory={() => setHistoryWf(w)}
                      onRun={() => handleRun(w)}
                      running={runningIds.has(w.id)}
                      linkedConversation={linkedConvMap[w.id] ?? null}
                      onContinueInBuilder={() => {
                        setNlBuilderConvId(linkedConvMap[w.id]?.id ?? null);
                        setNlBuilderOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Node legend */}
            {workflows.length > 0 && (
              <div className="px-6 py-3 border-t bg-muted/20 shrink-0">
                <div className="flex flex-wrap gap-4">
                  {(Object.entries(NODE_META) as [NodeType, (typeof NODE_META)[NodeType]][]).map(([type, meta]) => {
                    const Icon = meta.icon;
                    return (
                      <div key={type} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                        <span className="font-medium">{meta.label}:</span>
                        <span>{meta.description}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {activePanel === "tasks" && (
          <WorkflowTasksPanel workflows={workflows} filterDepartmentId={filterDepartmentId} />
        )}
      </div>

      {/* Dialogs */}
      <TemplateBrowserDialog
        open={templateBrowserOpen}
        onClose={() => setTemplateBrowserOpen(false)}
        templateType="workflow"
        title="Choose a Workflow Template"
        description="Browse pre-built workflow templates and customize them with AI"
        onSelectTemplate={(t) => {
          setTemplateBrowserOpen(false);
          setSelectedTemplate(t);
          setTemplatePromptOpen(true);
        }}
      />

      <TemplatePromptDialog
        open={templatePromptOpen}
        onClose={() => { setTemplatePromptOpen(false); setSelectedTemplate(null); }}
        template={selectedTemplate}
        generateLabel="Generate with AI"
        placeholder="Describe the workflow you want to build..."
        onGenerate={(prompt) => {
          setTemplatePromptOpen(false);
          setSelectedTemplate(null);
          setNlBuilderConvId(null);
          setNlBuilderInitialPrompt(prompt);
          setNlBuilderOpen(true);
        }}
      />

      <NLWorkflowBuilderDialog
        open={nlBuilderOpen}
        onClose={() => { setNlBuilderOpen(false); setNlBuilderConvId(null); setNlBuilderInitialPrompt(null); }}
        onOpenInEditor={handleOpenInEditor}
        initialConversationId={nlBuilderConvId}
        initialPrompt={nlBuilderInitialPrompt}
      />

      <WorkflowBuilderDialog
        open={builderOpen}
        onClose={() => {
          setBuilderOpen(false);
          setEditing(null);
          setRestoredDraft(null);
          setNlDraftNodes(null);
        }}
        onSaved={() => { fetchWorkflows(); setNlDraftNodes(null); }}
        editing={editing}
        restoredDraft={
          nlDraftNodes
            ? { name: nlDraftName, description: nlDraftDescription, nodes: nlDraftNodes, editingId: null }
            : restoredDraft
        }
      />

      {historyWf && (
        <ExecutionHistoryDialog workflow={historyWf} open={!!historyWf} onClose={() => setHistoryWf(null)} />
      )}
    </div>
  );
}
